import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, deleteField, deleteDoc, doc, getDoc, getDocs, limit, query, runTransaction, serverTimestamp, setDoc, where, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase-client';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import { useTeamMeta } from './events/hooks/useTeamMeta';
import { useRsvp } from './events/hooks/useRSVP';
import { getCurrencyMeta } from '../../../utils/currency';
import { estimateStripeFeeMinor, getStripeFeeConfig, grossUpForStripeFeeMinor, toMajorFromMinor, toMinorFromMajor } from '../../../utils/fees';
import { createNotification } from '../../../utils/createNotification';
import { getEventCommentThread } from '../../../utils/boardApi';
import { reportCaughtError } from '../../../utils/clientErrorReporting';
import { removeUndefinedFields } from '../../../utils/firestoreData';
import BasicFields from './events/sections/basicFields';
import RecurrenceFields from './events/sections/recurrenceFields';
import TagsSelector from './events/sections/tagSelector';
import EventModeFields from './events/sections/eventModeFields';
import AttendanceSection from './events/sections/attendance';
import TicketingSection from './events/sections/ticketing';
import RsvpDeadlineSection from './events/sections/rsvpDeadline';
import AttendeesSection from './events/sections/attendees';
import RefundsSection from './events/sections/refunds';
import CancelEventSection from './events/sections/cancelEvent';
import EventRegister from './events/EventRegister';
import ReadOnlyEventView from './events/readOnlyEventViews';
import RsvpControls from './events/RSVPControls';
import FamilyMemberRsvpPanels from './events/FamilyMemberRsvpPanels';
import TicketControls from './events/ticketControls';
import { useTicketPurchase } from './events/hooks/useTicketPurchase';
import EventCommentsSection from './events/EventCommentsSection';
import EventWaitlistControls from './events/EventWaitlistControls';
import {
  eventPostSaveWarning,
  eventSaveFailureMessage,
  isActionableEventSaveFailure,
} from './events/eventSaveFeedback';
import { resolveEventCommentOccurrenceId } from './events/eventCommentIdentity';
import { isRecurringRsvpEvent } from './events/readOnlyEventPriority';
import RolesEditor from './events/sections/rolesEditor';
import RolesReadOnly from './events/sections/rolesReadOnly';
import { isExpectedEventCommentActivityProbeFailure } from './events/eventCommentAccess';
import '../../../styles/events.css';
import { getEventTime } from '../../../utils/eventTiming';
import { getRecurrenceDatesForDay, parseDateIsoLocal } from '../../../utils/recurrence';
import { ticketAppliesToEvent } from '../../../utils/ticketCoverage';
import { resolveInitialAdmissionTicketsEnabled } from '../../../utils/admissionTicketDefaults';
import { formatEventPayoutTiming } from '../../../utils/eventPayoutTiming';
import { normalizeOptionalEventCapacity } from '../../../utils/eventCapacity';
import useStripePaymentGuard from '../../payments/useStripePaymentGuard';
import {
  DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES,
  DEFAULT_RSVP_REMINDER_OFFSET_MINUTES,
  formatRsvpDeadline,
  getEffectiveRsvpDeadline,
  getRsvpReminderSendAt,
  isRsvpDeadlinePassed,
  normalizeRsvpReminderOffsets,
} from '../../../utils/rsvpDeadline';
const safeConfirm = msg => typeof window !== 'undefined' ? window.confirm(msg) : false;
function SectionCard({
  title,
  subtitle,
  children
}) {
  return <div className="card" style={{
    marginTop: 16
  }}>
      {(title || subtitle) && <div className="card-header">
          {title && <h3 style={{
        margin: 0
      }}>{title}</h3>}
          {subtitle && <div className="card-subtitle">{subtitle}</div>}
        </div>}
      <div className="card-body">{children}</div>
    </div>;
}
function toOccurrenceId(startLike) {
  if (!startLike) return null;
  const d = new Date(startLike);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function toStartEnd(dateValue, timeValue, durationMinutes = 60) {
  if (!dateValue || !timeValue) return {
    start: null,
    end: null
  };
  const start = new Date(`${dateValue}T${timeValue}`);
  if (Number.isNaN(start.getTime())) return {
    start: null,
    end: null
  };
  const end = new Date(start.getTime() + Math.max(1, Number(durationMinutes || 60)) * 60 * 1000);
  return {
    start,
    end
  };
}
function toLocalDateObject(dateValue) {
  return parseDateIsoLocal(dateValue);
}
function toDateTimeLocalInput(value) {
  if (!value) return '';
  let date = null;
  if (typeof value.toDate === 'function') {
    date = value.toDate();
  } else if (value instanceof Date) {
    date = value;
  } else {
    date = new Date(value);
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
}
function dateFromDateTimeLocalInput(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function deriveDeadlineOffsetMinutes(eventData = {}, deadlineAt) {
  const deadline = dateFromDateTimeLocalInput(toDateTimeLocalInput(deadlineAt));
  if (!deadline) return null;
  const eventStart = toStartEnd(eventData.date, eventData.time || '00:00', eventData.durationMinutes || 60).start;
  if (!eventStart) return null;
  const minutes = Math.round((eventStart.getTime() - deadline.getTime()) / 60000);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}
function getConcreteDeadlineForReminderValidation({
  form,
  deadlineUsesOffset,
  deadlineOffsetMinutes,
  deadlineAt,
  isRecurringEvent,
  selectedOccurrenceId,
}) {
  if (!deadlineUsesOffset) return deadlineAt || null;
  const occurrenceDate = isRecurringEvent ? (selectedOccurrenceId || form.occurrenceId || '') : '';
  if (!occurrenceDate) return null;
  const eventStart = toStartEnd(occurrenceDate, form.time || form.recurrence?.time || '00:00', form.durationMinutes).start;
  if (!eventStart) return null;
  return new Date(eventStart.getTime() - deadlineOffsetMinutes * 60 * 1000);
}
function inferRecurrenceHintsFromDate(dateValue) {
  const d = toLocalDateObject(dateValue);
  if (!d) return null;
  const dayOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][d.getDay()];
  const dayOfMonth = d.getDate();
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const isLastWeekdayInMonth = dayOfMonth + 7 > daysInMonth;
  const weekOfMonth = isLastWeekdayInMonth ? 'Last' : ['First', 'Second', 'Third', 'Fourth'][Math.max(0, Math.min(3, Math.floor((dayOfMonth - 1) / 7)))];
  return {
    dayOfWeek,
    weekOfMonth
  };
}
function inferRecurrenceHintsFromEvent(data) {
  if (!data) return null;
  if (typeof data.date === 'string' && data.date.trim() !== '') {
    const hinted = inferRecurrenceHintsFromDate(data.date.trim());
    if (hinted) return hinted;
  }
  const startCandidate = data.start?.toDate ? data.start.toDate() : data.start ? new Date(data.start) : null;
  if (startCandidate && !Number.isNaN(startCandidate.getTime())) {
    const y = startCandidate.getFullYear();
    const m = String(startCandidate.getMonth() + 1).padStart(2, '0');
    const day = String(startCandidate.getDate()).padStart(2, '0');
    return inferRecurrenceHintsFromDate(`${y}-${m}-${day}`);
  }
  return null;
}
function inferEventTypeFromData(data) {
  if (!data || typeof data !== 'object') return 'oneOff';
  if (data.scope === 'occurrence' || data.isOccurrenceOverride) return 'oneOff';
  if (typeof data.type === 'string' && (data.type === 'oneOff' || data.type === 'recurring')) {
    return data.type;
  }
  const hasRecurrence = (Array.isArray(data.recurrence?.byDay) && data.recurrence.byDay.length > 0) || !!data.recurrence?.frequency || !!data.frequency || !!data.dayOfWeek || !!data.weekOfMonth;
  return hasRecurrence ? 'recurring' : 'oneOff';
}
function getDefaultOccurrenceId(d) {
  if (!d) return null;
  if (d.occurrenceId) return d.occurrenceId;
  const directStart = d.start?.toDate ? d.start.toDate() : d.start ? new Date(d.start) : null;
  if (directStart && !Number.isNaN(directStart.getTime())) return toOccurrenceId(directStart);
  if (d.date) {
    const dateStart = new Date(`${d.date}T${d.time || '00:00'}`);
    if (!Number.isNaN(dateStart.getTime())) return toOccurrenceId(dateStart);
  }
  const byDay = d.recurrence?.byDay?.[0];
  if (!byDay) return null;
  const next = getRecurrenceDatesForDay({ dayCode: byDay, recurrence: { ...d.recurrence, frequency: d.recurrence?.frequency || d.frequency || 'WEEKLY' }, maxOccurrences: 1 })[0];
  return next ? toOccurrenceId(next) : null;
}
function normalizeMemberTags(rawTags) {
  if (Array.isArray(rawTags)) {
    return rawTags.filter(t => typeof t === 'string' && t.trim() !== '').map(t => t.trim());
  }
  if (rawTags && typeof rawTags === 'object') {
    return Object.entries(rawTags).filter(([name, enabled]) => Boolean(enabled) && typeof name === 'string' && name.trim() !== '').map(([name]) => name.trim());
  }
  return [];
}
function displayNameFromUserDoc(uid, data) {
  const direct = typeof data?.displayName === 'string' ? data.displayName.trim() : '';
  if (direct) return direct;
  const first = typeof data?.firstName === 'string' ? data.firstName.trim() : '';
  const last = typeof data?.lastName === 'string' ? data.lastName.trim() : '';
  const full = [first, last].filter(Boolean).join(' ').trim();
  if (full) return full;
  const email = typeof data?.email === 'string' ? data.email.trim() : '';
  if (email) return email;
  return 'Member';
}
function sortOutingsByUpdatedAt(a, b) {
  const left = a?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  const right = b?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  return right - left;
}
function createVacantSlot() {
  return {
    id: crypto.randomUUID(),
    assigneeUserId: null,
    assigneeDisplayName: ''
  };
}
function normalizeRoleSlot(rawSlot) {
  const id = typeof rawSlot?.id === 'string' && rawSlot.id.trim() !== '' ? rawSlot.id.trim() : crypto.randomUUID();
  const assigneeUserId = typeof rawSlot?.assigneeUserId === 'string' && rawSlot.assigneeUserId.trim() !== '' ? rawSlot.assigneeUserId.trim() : null;
  const assigneeDisplayName = typeof rawSlot?.assigneeDisplayName === 'string' ? rawSlot.assigneeDisplayName.trim() : '';
  return {
    ...(rawSlot && typeof rawSlot === 'object' ? rawSlot : {}),
    id,
    assigneeUserId,
    assigneeDisplayName
  };
}
function normalizeEventRoles(rawRoles) {
  if (!Array.isArray(rawRoles)) return [];
  return rawRoles.map(rawRole => {
    const roleName = typeof rawRole?.name === 'string' ? rawRole.name.trim() : '';
    const roleId = typeof rawRole?.id === 'string' && rawRole.id.trim() !== '' ? rawRole.id.trim() : crypto.randomUUID();
    const allowedTags = Array.isArray(rawRole?.allowedTags) ? Array.from(new Set(rawRole.allowedTags.filter(tagName => typeof tagName === 'string' && tagName.trim() !== '').map(tagName => tagName.trim()))) : [];
    const incomingSlots = Array.isArray(rawRole?.slots) ? rawRole.slots : [];
    const normalizedSlots = incomingSlots.map(normalizeRoleSlot);
    const slotCount = Math.max(1, normalizedSlots.length || Number(rawRole?.defaultSlots) || 1);
    const slots = normalizedSlots.slice(0, slotCount);
    while (slots.length < slotCount) {
      slots.push(createVacantSlot());
    }
    return {
      ...(rawRole && typeof rawRole === 'object' ? rawRole : {}),
      id: roleId,
      name: roleName,
      requiresApproval: rawRole?.requiresApproval !== false,
      allowedTags,
      slots
    };
  }).filter(role => role.name !== '');
}
function resizeRoleSlots(role, nextCountRaw) {
  const nextCount = Math.max(1, Number(nextCountRaw) || 1);
  const slots = Array.isArray(role?.slots) ? role.slots.map(normalizeRoleSlot) : [];
  const assignedCount = slots.filter(slot => slot.assigneeUserId).length;
  if (nextCount < assignedCount) {
    throw new Error('Cannot set slots below the number of assigned members.');
  }
  const next = slots.slice(0, nextCount);
  while (next.length < nextCount) {
    next.push(createVacantSlot());
  }
  return next;
}
function applyRoleSlotAssignment(roles, roleId, slotId, assignment) {
  return roles.map(role => {
    if (role.id !== roleId) return role;
    return {
      ...role,
      slots: (role.slots || []).map(slot => {
        if (slot.id !== slotId) return slot;
        return {
          ...slot,
          assigneeUserId: assignment?.assigneeUserId || null,
          assigneeDisplayName: assignment?.assigneeDisplayName || '',
          responseStatus: assignment?.assigneeUserId
            ? (assignment?.responseStatus ?? slot.responseStatus ?? null)
            : null
        };
      })
    };
  });
}
function buildOccurrenceEventDocId(seriesEventId, occurrenceId) {
  if (!seriesEventId || !occurrenceId) return null;
  return `${seriesEventId}__${occurrenceId}`;
}
export default function EventFormModal({
  teamId,
  isOpen,
  onClose,
  onCopy,
  initialData,
  readOnly = false,
  userId,
  isAdmin = false,
  canModerateEventComments = false,
  canManageEventRefunds = false,
  canCheckIn = false,
  teamBrand = {},
  focusComments = false,
  calendarSurface = 'team'
}) {
  const { requireStripePayment, stripeGuardDialog } = useStripePaymentGuard(teamId);
  const isExisting = !!(initialData?.id || initialData?.originalId);
  const isCopy = !isExisting && Boolean(initialData?._copyToken);
  const [editMode, setEditMode] = useState(!isExisting);
  const readOnlyComputed = readOnly || !editMode;
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState(null);
  const [refundRequested, setRefundRequested] = useState(false);
  const [hasPaidTickets, setHasPaidTickets] = useState(false);
  const [roleActionBusy, setRoleActionBusy] = useState(false);
  const [rolesEditorExpanded, setRolesEditorExpanded] = useState(false);
  const [editScopeIntent, setEditScopeIntent] = useState('series');
  const [currentUserName, setCurrentUserName] = useState('');
  const [memberTagNames, setMemberTagNames] = useState([]);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [roleRequests, setRoleRequests] = useState([]);
  const [occurrenceRoleRequests, setOccurrenceRoleRequests] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [savedRolesSnapshot, setSavedRolesSnapshot] = useState([]);
  const [rsvpMemberOptions, setRsvpMemberOptions] = useState([]);
  const [linkedOuting, setLinkedOuting] = useState(null);
  const [linkedOutingCrews, setLinkedOutingCrews] = useState([]);
  const [linkedOutingLoading, setLinkedOutingLoading] = useState(false);
  const [teamEventCommentsEnabled, setTeamEventCommentsEnabled] = useState(false);
  const [teamRsvpSeriesResponsesEnabled, setTeamRsvpSeriesResponsesEnabled] = useState(true);
  const [teamEventCommentsLoading, setTeamEventCommentsLoading] = useState(false);
  const [eventCommentOccurrenceId, setEventCommentOccurrenceId] = useState(null);
  const [eventCommentIdentityLoading, setEventCommentIdentityLoading] = useState(false);
  const [eventCommentsHaveActivity, setEventCommentsHaveActivity] = useState(false);
  const [eventCommentsActivityLoading, setEventCommentsActivityLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    id: '',
    name: '',
    type: 'oneOff',
    date: '',
    time: '',
    dayOfWeek: 'Wednesday',
    weekOfMonth: 'First',
    frequency: 'WEEKLY',
    recurrenceInterval: 1,
    recurrenceStartDate: '',
    recurrenceEndDate: '',
    durationMinutes: 60,
    tags: [],
    eventMode: 'inPerson',
    onlineUrl: '',
    locationId: '',
    saveLocation: false,
    locationName: '',
    locationStreet: '',
    locationCity: '',
    locationPostcode: '',
    locationCountry: '',
    attendanceMode: 'none',
    capacity: '',
    waitlistMode: 'off',
    waitlistClaimWindowHours: 24,
    rsvpDeadlineEnabled: false,
    rsvpDeadlineAt: '',
    rsvpDeadlineOffsetMinutes: DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES,
    rsvpReminderEnabled: false,
    rsvpReminderOffsetMinutes: DEFAULT_RSVP_REMINDER_OFFSET_MINUTES,
    rsvpReminderOffsetMinutesList: [DEFAULT_RSVP_REMINDER_OFFSET_MINUTES],
    allowNonMembers: false,
    externalAttendeeMode: 'individual_accounts',
    externalInfoPreset: 'safety',
    publicDescription: '',
    publicSlug: '',
    publicEventsSlug: '',
    externalConfigurationLocked: false,
    admissionTicketsEnabled: true,
    admissionTicketingLocked: false,
    ticketName: '',
    ticketCurrency: 'GBP',
    ticketPriceMajor: '',
    ticketPricePence: undefined,
    ticketTypes: [],
    extras: [],
    bookingForm: null,
    protectedAnswerRetentionDays: 30,
    controlSalesTime: false,
    ticketSaleStart: '',
    ticketSaleEnd: '',
    ticketPlatformFeeHandling: 'add',
    ticketPaymentBasis: 'per_occurrence',
    ticketSaleOpenDaysBefore: 30,
    ticketSaleCloseHoursBefore: 0,
    stripeProductId: '',
    stripePriceId: '',
    checkoutUrl: '',
    roles: []
  });
  const previousTypeRef = useRef('oneOff');
  const takeRoleCallable = useMemo(() => httpsCallable(functions, 'takeOccurrenceRole'), []);
  const respondDutyCallable = useMemo(() => httpsCallable(functions, 'respondDutyAssignment'), []);
  const createDutySwapCallable = useMemo(() => httpsCallable(functions, 'createDutySwapRequest'), []);
  const cancelEventScopeCallable = useMemo(() => httpsCallable(functions, 'cancelEventScope'), []);
  useEffect(() => {
    setEditMode(!isExisting);
  }, [isExisting]);
  useEffect(() => {
    setRolesEditorExpanded(false);
  }, [isExisting, isOpen]);
  useEffect(() => {
    setForm(prev => ({ ...prev, _canCheckIn: canCheckIn }));
  }, [canCheckIn]);
  useEffect(() => {
    setEditScopeIntent('series');
  }, [isOpen, initialData?.id, initialData?.originalId]);
  const [appPaymentCfg, setAppPaymentCfg] = useState(null);
  useEffect(() => {
    getDoc(doc(db, 'appSettings', 'payment')).then(snap => setAppPaymentCfg(snap.exists() ? snap.data() : null)).catch(() => setAppPaymentCfg(null));
  }, []);
  useEffect(() => {
    if (!isOpen || !teamId) {
      setTeamEventCommentsEnabled(false);
      setTeamRsvpSeriesResponsesEnabled(true);
      setTeamEventCommentsLoading(false);
      return;
    }
    let cancelled = false;
    setTeamEventCommentsLoading(true);
    getDoc(doc(db, 'teams', teamId)).then(snap => {
      if (cancelled) return;
      const eventSettings = snap.exists() ? snap.data()?.eventSettings || {} : {};
      if (snap.exists() && snap.data()?.publicEventsSlug) {
        setForm(prev => ({ ...prev, publicEventsSlug: snap.data().publicEventsSlug }));
      }
      setTeamEventCommentsEnabled(Boolean(eventSettings.commentsEnabled));
      setTeamRsvpSeriesResponsesEnabled(eventSettings.rsvpSeriesResponsesEnabled !== false);
    }).catch((err) => {
      reportCaughtError(err, {
        source: 'event-comments',
        severity: 'error',
        context: {
          phase: 'settings-load',
          teamId,
        },
      });
      if (!cancelled) {
        setTeamEventCommentsEnabled(false);
        setTeamRsvpSeriesResponsesEnabled(true);
      }
    }).finally(() => {
      if (!cancelled) setTeamEventCommentsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId]);
  useEffect(() => {
    const eventId = form.originalEventId || form.originalId || form.id;
    const occurrenceId = selectedOccurrenceId || form.occurrenceId || null;
    if (!isOpen || !teamId || !eventId || !occurrenceId) {
      setEventCommentOccurrenceId(null);
      setEventCommentIdentityLoading(false);
      return undefined;
    }

    let cancelled = false;
    setEventCommentIdentityLoading(true);
    getDoc(doc(db, 'teams', teamId, 'events', eventId)).then((snap) => {
      if (cancelled) return;
      setEventCommentOccurrenceId(snap.exists()
        ? resolveEventCommentOccurrenceId(snap.data() || {}, occurrenceId)
        : occurrenceId);
    }).catch((err) => {
      if (!isExpectedEventCommentActivityProbeFailure(err)) {
        reportCaughtError(err, {
          source: 'event-comments',
          severity: 'error',
          context: { phase: 'identity-load', teamId, eventId, occurrenceId },
        });
      }
      if (!cancelled) setEventCommentOccurrenceId(occurrenceId);
    }).finally(() => {
      if (!cancelled) setEventCommentIdentityLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [form.id, form.occurrenceId, form.originalEventId, form.originalId, isOpen, selectedOccurrenceId, teamId]);
  useEffect(() => {
    const commentsEventId = form.originalEventId || form.originalId || form.id;
    const commentsOccurrenceId = eventCommentOccurrenceId;
    const topicId = commentsOccurrenceId ? `${commentsEventId}__${commentsOccurrenceId}` : commentsEventId;
    if (!isOpen || !teamId || !teamEventCommentsEnabled || eventCommentIdentityLoading || !topicId) {
      setEventCommentsHaveActivity(false);
      setEventCommentsActivityLoading(false);
      return undefined;
    }
    setEventCommentsActivityLoading(true);
    let cancelled = false;
    getEventCommentThread({
      teamId,
      eventId: commentsEventId,
      occurrenceId: commentsOccurrenceId,
    }).then((result) => {
      if (cancelled) return;
      setEventCommentsHaveActivity(Boolean(result?.topic && result.topic.moderationStatus !== 'deleted'));
    }).catch((err) => {
      if (!cancelled) {
        if (!isExpectedEventCommentActivityProbeFailure(err)) {
          reportCaughtError(err, {
            source: 'event-comments',
            severity: 'error',
            context: {
              phase: 'activity-load',
              teamId,
              eventId: commentsEventId,
              occurrenceId: commentsOccurrenceId || '',
              topicId,
              code: err?.code || '',
            },
          });
        }
        setEventCommentsHaveActivity(false);
      }
    }).finally(() => {
      if (!cancelled) setEventCommentsActivityLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    form.id,
    form.occurrenceId,
    form.originalEventId,
    form.originalId,
    eventCommentIdentityLoading,
    eventCommentOccurrenceId,
    isOpen,
    selectedOccurrenceId,
    teamEventCommentsEnabled,
    teamId,
  ]);
  const keepIfNonEmpty = useCallback(value => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string' && value.trim() === '') return undefined;
    return value;
  }, []);
  const notifyTeamAdmins = useCallback(async ({
    message,
    actionUrl
  }) => {
    try {
      const teamSnap = await getDoc(doc(db, 'teams', teamId));
      if (!teamSnap.exists()) return;
      const adminIds = Array.isArray(teamSnap.data()?.admins) ? Array.from(new Set(teamSnap.data().admins.filter(uid => typeof uid === 'string' && uid.trim() !== ''))) : [];
      if (adminIds.length === 0) {
        return;
      }
      await Promise.all(adminIds.map(uid => createNotification({
        userId: uid,
        message,
        actionUrl,
        teamId,
        notificationCategory: 'admin',
        pushContext: form.name || 'Event',
      }).catch(err => {})));
    } catch (err) {}
  }, [form.name, teamId]);
  const notifyEligibleMembersForNewVacancies = useCallback(async ({
    eventId,
    eventName,
    previousRoles,
    nextRoles
  }) => {
    if (!teamId || !eventId) return;
    const previousSlotIds = new Set();
    (previousRoles || []).forEach(role => {
      (role?.slots || []).forEach(slot => {
        if (typeof slot?.id === 'string' && slot.id.trim() !== '') {
          previousSlotIds.add(slot.id.trim());
        }
      });
    });
    const newVacancies = [];
    (nextRoles || []).forEach(role => {
      const roleName = typeof role?.name === 'string' && role.name.trim() !== '' ? role.name.trim() : 'Role';
      const allowedTags = Array.isArray(role?.allowedTags) ? role.allowedTags.filter(tag => typeof tag === 'string' && tag.trim() !== '') : [];
      (role?.slots || []).forEach((slot, slotIndex) => {
        const slotId = typeof slot?.id === 'string' && slot.id.trim() !== '' ? slot.id.trim() : `${role?.id || roleName}:slot:${slotIndex}`;
        if (previousSlotIds.has(slotId)) return;
        if (slot?.assigneeUserId) return;
        newVacancies.push({
          roleName,
          allowedTags
        });
      });
    });
    if (newVacancies.length === 0) return;
    const membersSnap = await getDocs(collection(db, 'teams', teamId, 'members'));
    const notifyTasks = [];
    membersSnap.forEach(memberDoc => {
      const memberId = memberDoc.id;
      if (!memberId) return;
      const memberTags = normalizeMemberTags(memberDoc.data()?.tags);
      const eligibleRoles = Array.from(new Set(newVacancies.filter(vacancy => {
        const allowedTags = vacancy.allowedTags || [];
        if (allowedTags.length === 0) return true;
        return allowedTags.some(tagName => memberTags.includes(tagName));
      }).map(vacancy => vacancy.roleName)));
      if (eligibleRoles.length === 0) return;
      const rolePreview = eligibleRoles.length > 3 ? `${eligibleRoles.slice(0, 3).join(', ')}…` : eligibleRoles.join(', ');
      const message = eligibleRoles.length === 1 ? `New role vacancy: ${rolePreview} on ${eventName || 'an event'}.` : `New role vacancies on ${eventName || 'an event'}: ${rolePreview}.`;
      notifyTasks.push(createNotification({
        userId: memberId,
        message,
        actionUrl: `/teams/${teamId}/calendar?event=${eventId}&panel=roles`,
        teamId,
        notificationCategory: 'events',
        pushContext: eventName || 'Event',
        pushPreview: message,
      }).catch(err => {}));
    });
    await Promise.all(notifyTasks);
  }, [teamId]);
  const normalisePriceIntoMajor = useCallback((pence, currency) => {
    const {
      decimals
    } = getCurrencyMeta(currency || 'GBP');
    if (typeof pence === 'number') return (pence / Math.pow(10, decimals)).toFixed(decimals);
    const n = Number(pence);
    if (!Number.isNaN(n) && n > 0) {
      return (n / Math.pow(10, decimals)).toFixed(decimals);
    }
    return '';
  }, []);
  const normaliseMajorIntoPence = useCallback((major, currency) => {
    const {
      decimals
    } = getCurrencyMeta(currency || 'GBP');
    const s = String(major ?? '').trim();
    if (!s) return undefined;
    const n = Number(s);
    if (Number.isNaN(n)) return undefined;
    return Math.round(n * Math.pow(10, decimals));
  }, []);
  const {
    tags,
    locations,
    eventRoles,
    teamCountry
  } = useTeamMeta(teamId, isOpen);
  useEffect(() => {
    if (!isOpen) return;
    setForm(prev => ({
      ...prev,
      locationCountry: prev.locationCountry || teamCountry || 'UK'
    }));
  }, [isOpen, teamCountry]);
  useEffect(() => {
    if (!isOpen || !teamId || !(form.originalEventId || form.id)) {
      setLinkedOuting(null);
      setLinkedOutingCrews([]);
      setLinkedOutingLoading(false);
      return;
    }
    let cancelled = false;
    const loadLinkedOuting = async () => {
      setLinkedOutingLoading(true);
      try {
        const eventId = form.originalEventId || form.id;
        const outingQuery = isAdmin ? query(collection(db, 'teams', teamId, 'outings'), where('linkedEventId', '==', eventId)) : query(collection(db, 'teams', teamId, 'outings'), where('linkedEventId', '==', eventId), where('status', '==', 'submitted'));
        const outingsSnap = await getDocs(outingQuery);
        if (cancelled) return;
        const outings = outingsSnap.docs
          .map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data()
          }))
          .sort(sortOutingsByUpdatedAt);
        const selectedOuting = outings[0] || null;
        setLinkedOuting(selectedOuting);
        if (!selectedOuting) {
          setLinkedOutingCrews([]);
          return;
        }
        const crewsSnap = await getDocs(collection(db, 'teams', teamId, 'outings', selectedOuting.id, 'crews'));
        if (cancelled) return;
        setLinkedOutingCrews(crewsSnap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        })));
      } catch (err) {
        if (!cancelled) {
          setLinkedOuting(null);
          setLinkedOutingCrews([]);
        }
      } finally {
        if (!cancelled) setLinkedOutingLoading(false);
      }
    };
    loadLinkedOuting();
    return () => {
      cancelled = true;
    };
  }, [form.id, form.originalEventId, isAdmin, isOpen, teamId]);
  useEffect(() => {
    if (!initialData) {
      setSavedRolesSnapshot([]);
      return;
    }
    const inferAttendanceMode = d => {
      if (d.attendanceMode === 'ticketed') return 'ticketed';
      if (d.attendanceMode === 'rsvp' || d.requiresRsvp) return 'rsvp';
      return 'none';
    };
    const isOnline = !!initialData.onlineUrl;
    const currency = initialData.ticketCurrency || 'GBP';
    const inferredHints = inferRecurrenceHintsFromEvent(initialData);
    const inferredType = inferEventTypeFromData(initialData);
    const defaultOccurrenceId = getDefaultOccurrenceId(initialData);
    const shouldUseOccurrenceId = inferredType === 'recurring' || initialData.scope === 'occurrence' || Boolean(initialData.isOccurrenceOverride) || Boolean(initialData.occurrenceId);
    setSelectedOccurrenceId(shouldUseOccurrenceId ? defaultOccurrenceId : null);
    const penceField = typeof initialData.ticketPricePence === 'number' ? initialData.ticketPricePence : typeof initialData.ticketPriceMinor === 'number' ? initialData.ticketPriceMinor : undefined;
    const hasExistingMajor = initialData.ticketPriceMajor != null && String(initialData.ticketPriceMajor).trim() !== '';
    const major = hasExistingMajor ? String(initialData.ticketPriceMajor) : normalisePriceIntoMajor(penceField, currency);
    const loc = initialData.location || {};
    const locName = loc.name ?? initialData.locationName ?? '';
    const locStreet = loc.street ?? initialData.locationStreet ?? '';
    const locCity = loc.city ?? initialData.locationCity ?? '';
    const locPostcode = loc.postcode ?? initialData.locationPostcode ?? '';
    const locCountry = loc.country ?? initialData.locationCountry ?? '';
    const hasSalesWindow = !!(initialData.ticketSaleStart || initialData.ticketSaleEnd);
    const rsvpDeadline = initialData.rsvpDeadline || {};
    const rsvpReminder = initialData.rsvpReminder || {};
    setForm(prev => ({
      ...prev,
      ...initialData,
      id: initialData.id || initialData.originalId || prev.id || '',
      type: inferredType,
      time: getEventTime(initialData),
      tags: initialData.tags || prev.tags || [],
      dayOfWeek: initialData.recurrence?.byDay?.[0] ? {
        MO: 'Monday',
        TU: 'Tuesday',
        WE: 'Wednesday',
        TH: 'Thursday',
        FR: 'Friday',
        SA: 'Saturday',
        SU: 'Sunday'
      }[initialData.recurrence.byDay[0]] || 'Wednesday' : inferredHints?.dayOfWeek || prev.dayOfWeek || 'Wednesday',
      weekOfMonth: initialData.recurrence?.weekOfMonth || inferredHints?.weekOfMonth || prev.weekOfMonth || 'First',
      frequency: String(initialData.recurrence?.frequency || initialData.frequency || 'WEEKLY').toUpperCase(),
      recurrenceInterval: initialData.recurrence?.interval || 1,
      recurrenceStartDate: initialData.recurrence?.startDate || '',
      recurrenceEndDate: keepIfNonEmpty(initialData.recurrence?.endDate) ?? keepIfNonEmpty(initialData.recurrenceEndDate) ?? prev.recurrenceEndDate ?? '',
      eventMode: isOnline ? 'online' : prev.eventMode || 'inPerson',
      onlineUrl: keepIfNonEmpty(initialData.onlineUrl) ?? prev.onlineUrl ?? '',
      locationName: keepIfNonEmpty(locName) ?? prev.locationName ?? '',
      locationStreet: keepIfNonEmpty(locStreet) ?? prev.locationStreet ?? '',
      locationCity: keepIfNonEmpty(locCity) ?? prev.locationCity ?? '',
      locationPostcode: keepIfNonEmpty(locPostcode) ?? prev.locationPostcode ?? '',
      locationCountry: keepIfNonEmpty(locCountry) ?? prev.locationCountry ?? '',
      attendanceMode: inferAttendanceMode(initialData),
      capacity: keepIfNonEmpty(initialData.capacity) ?? prev.capacity ?? '',
      waitlistMode: ['manual', 'automatic'].includes(initialData.waitlist?.mode) ? initialData.waitlist.mode : 'off',
      waitlistClaimWindowHours: Math.min(168, Math.max(1, Number(initialData.waitlist?.claimWindowHours || 24))),
      rsvpDeadlineEnabled: rsvpDeadline.enabled === true,
      rsvpDeadlineAt: toDateTimeLocalInput(rsvpDeadline.deadlineAt) || prev.rsvpDeadlineAt || '',
      rsvpDeadlineOffsetMinutes: rsvpDeadline.offsetMinutesBeforeStart || deriveDeadlineOffsetMinutes(initialData, rsvpDeadline.deadlineAt) || prev.rsvpDeadlineOffsetMinutes || DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES,
      rsvpReminderEnabled: rsvpReminder.enabled === true,
      rsvpReminderOffsetMinutes: rsvpReminder.offsetMinutesBeforeDeadline || prev.rsvpReminderOffsetMinutes || DEFAULT_RSVP_REMINDER_OFFSET_MINUTES,
      rsvpReminderOffsetMinutesList: normalizeRsvpReminderOffsets(rsvpReminder),
      allowNonMembers: !!initialData.allowNonMembers,
      externalAttendeeMode: initialData.externalAttendeeMode || 'individual_accounts',
      externalInfoPreset: initialData.externalInfoPreset || 'safety',
      publicDescription: keepIfNonEmpty(initialData.publicDescription) ?? '',
      publicSlug: keepIfNonEmpty(initialData.publicSlug) ?? '',
      externalConfigurationLocked: initialData.externalConfigurationLocked === true,
      // Existing events remain explicit opt-in; genuinely new paid events
      // default to admission passes even when a launcher omitted the field.
      admissionTicketsEnabled: resolveInitialAdmissionTicketsEnabled(initialData, inferAttendanceMode(initialData)),
      admissionTicketingLocked: initialData.admissionTicketingLocked === true,
      ticketName: keepIfNonEmpty(initialData.ticketName) ?? prev.ticketName ?? '',
      ticketCurrency: currency,
      ticketPriceMajor: major || prev.ticketPriceMajor || '',
      ticketPricePence: typeof penceField === 'number' ? penceField : prev.ticketPricePence,
      ticketTypes: Array.isArray(initialData.ticketTypes) ? initialData.ticketTypes : [],
      extras: Array.isArray(initialData.extras) ? initialData.extras : [],
      bookingForm: initialData.bookingForm || null,
      protectedAnswerRetentionDays: Math.max(1, Number(initialData.protectedAnswerRetentionDays || 30)),
      controlSalesTime: hasSalesWindow,
      ticketSaleStart: keepIfNonEmpty(initialData.ticketSaleStart) ?? prev.ticketSaleStart ?? '',
      ticketSaleEnd: keepIfNonEmpty(initialData.ticketSaleEnd) ?? prev.ticketSaleEnd ?? '',
      ticketPlatformFeeHandling: initialData.ticketPlatformFeeHandling || prev.ticketPlatformFeeHandling || 'add',
      ticketPaymentBasis: initialData.ticketPaymentBasis || prev.ticketPaymentBasis || 'per_occurrence',
      ticketSaleOpenDaysBefore: initialData.ticketSaleOpenDaysBefore ?? prev.ticketSaleOpenDaysBefore ?? 30,
      ticketSaleCloseHoursBefore: initialData.ticketSaleCloseHoursBefore ?? prev.ticketSaleCloseHoursBefore ?? 0,
      stripeProductId: keepIfNonEmpty(initialData.stripeProductId) ?? prev.stripeProductId ?? '',
      stripePriceId: keepIfNonEmpty(initialData.stripePriceId) ?? prev.stripePriceId ?? '',
      checkoutUrl: keepIfNonEmpty(initialData.checkoutUrl) ?? prev.checkoutUrl ?? '',
      roles: normalizeEventRoles(initialData.roles || prev.roles || [])
    }));
    setSavedRolesSnapshot(normalizeEventRoles(initialData.roles || []));
  }, [initialData, keepIfNonEmpty, normalisePriceIntoMajor]);
  useEffect(() => {
    const previousType = previousTypeRef.current;
    const enteringRecurring = previousType !== 'recurring' && form.type === 'recurring';
    if (enteringRecurring) {
      const hinted = inferRecurrenceHintsFromDate(form.date);
      if (hinted) {
        setForm(prev => ({
          ...prev,
          dayOfWeek: hinted.dayOfWeek || prev.dayOfWeek,
          weekOfMonth: hinted.weekOfMonth || prev.weekOfMonth
        }));
      }
    }
    previousTypeRef.current = form.type;
  }, [form.type, form.date]);
  useEffect(() => {
    if (!isOpen || !isExisting || !isAdmin || !form.id) {
      setHasPaidTickets(false);
      return;
    }
    const checkPaidTickets = async () => {
      try {
        const ticketsRef = collection(db, 'teams', teamId, 'events', form.id, 'tickets');
        const q = query(ticketsRef, where('status', 'in', ['paid', 'partially_refunded']), limit(1));
        const snap = await getDocs(q);
        setHasPaidTickets(!snap.empty);
      } catch (err) {
        setHasPaidTickets(false);
      }
    };
    checkPaidTickets();
  }, [isOpen, isExisting, isAdmin, teamId, form.id]);
  useEffect(() => {
    if (!form.locationId) return;
    const loc = locations?.find(entry => entry.id === form.locationId);
    if (!loc) return;
    setForm(prev => ({
      ...prev,
      eventMode: prev.eventMode === 'online' ? prev.eventMode : 'inPerson',
      locationName: loc.name ?? '',
      locationStreet: loc.street ?? '',
      locationCity: loc.city ?? '',
      locationPostcode: loc.postcode ?? '',
      locationCountry: loc.country ?? prev.locationCountry ?? teamCountry ?? 'UK'
    }));
  }, [form.locationId, locations, teamCountry]);
  useEffect(() => {
    if (!isOpen || !teamId || !userId) {
      setRsvpMemberOptions([]);
      return;
    }
    let cancelled = false;
    const loadRsvpMemberOptions = async () => {
      try {
        const requesterUserSnap = await getDoc(doc(db, 'users', userId));
        const requesterUserData = requesterUserSnap.exists() ? requesterUserSnap.data() : {};
        const requesterFamilyRoles = requesterUserData?.family_roles || {};
        const candidateIds = new Set([userId]);
        const parentFamilyIds = Object.entries(requesterFamilyRoles).filter(([, role]) => role === 'parent').map(([familyId]) => familyId);
        await Promise.all(parentFamilyIds.map(async familyId => {
          try {
            const membersSnap = await getDocs(collection(db, 'families', familyId, 'members'));
            membersSnap.forEach(memberSnap => {
              const role = memberSnap.data()?.role;
              if (role === 'parent' || role === 'child') {
                candidateIds.add(memberSnap.id);
              }
            });
          } catch (err) {}
        }));
        const rows = await Promise.all(Array.from(candidateIds).map(async candidateId => {
          try {
            const snap = await getDoc(doc(db, 'users', candidateId));
            const data = snap.exists() ? snap.data() : {};
            const teamRoles = data?.team_roles || {};
            const inTeam = candidateId === userId || Boolean(teamRoles?.[teamId]);
            if (!inTeam) return null;
            const name = displayNameFromUserDoc(candidateId, data);
            return {
              id: candidateId,
              name
            };
          } catch {
            if (candidateId !== userId) return null;
            return {
              id: userId,
              name: 'You'
            };
          }
        }));
        let options = rows.filter(Boolean);
        if (!options.some(entry => entry.id === userId)) {
          options = [{
            id: userId,
            name: 'You'
          }, ...options];
        }
        options.sort((a, b) => {
          if (a.id === userId) return -1;
          if (b.id === userId) return 1;
          return a.name.localeCompare(b.name);
        });
        if (cancelled) return;
        setRsvpMemberOptions(options);
      } catch (err) {
        if (cancelled) return;
        const fallback = [{
          id: userId,
          name: 'You'
        }];
        setRsvpMemberOptions(fallback);
      }
    };
    loadRsvpMemberOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId, userId]);
  const familyRsvpMembers = useMemo(() => rsvpMemberOptions.filter(member => member.id !== userId), [rsvpMemberOptions, userId]);
  const attendanceEventId = form.originalEventId || form.originalId || form.id;
  const attendanceForm = useMemo(
    () => attendanceEventId && attendanceEventId !== form.id ? { ...form, id: attendanceEventId } : form,
    [attendanceEventId, form]
  );
  const {
    myRsvpStatus,
    myOccurrenceRsvpStatus,
    mySeriesRsvpStatus,
    rsvpLoading,
    rsvpBusy,
    currentYesCount,
    capacityRemaining,
    capacityNum,
    isFull,
    isRecurring,
    occurrenceId,
    setRsvpStatus,
    resetRsvpStatus
  } = useRsvp({
    form: attendanceForm,
    teamId,
    userId,
    occurrenceIdOverride: selectedOccurrenceId,
    rsvpSeriesResponsesEnabled: teamRsvpSeriesResponsesEnabled
  });
  const fallbackTicketMeta = useMemo(() => ({
    name: form.ticketName || 'General Admission',
    price: keepIfNonEmpty(form.ticketPriceMajor) ?? normalisePriceIntoMajor(form.ticketPricePence, form.ticketCurrency) ?? '',
    currency: form.ticketCurrency || 'GBP'
  }), [form.ticketName, form.ticketPriceMajor, form.ticketPricePence, form.ticketCurrency, keepIfNonEmpty, normalisePriceIntoMajor]);
  const teamFeeCfg = undefined;
  const ticketPricing = useMemo(() => {
    const currency = form.ticketCurrency || 'GBP';
    const unitMinor = typeof form.ticketPricePence === 'number' && form.ticketPricePence > 0 ? form.ticketPricePence : toMinorFromMajor(form.ticketPriceMajor, currency);
    if (!unitMinor) return null;
    let stripeCfg = getStripeFeeConfig({
      currency,
      teamFeeCfg
    });
    if (!stripeCfg) {
      const cur = String(currency).toLowerCase();
      stripeCfg = {
        percent: cur === 'usd' ? 2.9 : 1.5,
        fixedMinor: cur === 'usd' ? 30 : cur === 'eur' ? 25 : 20
      };
    }
    const stripeFeeMinor = estimateStripeFeeMinor(unitMinor, stripeCfg);
    const configuredTicketFeeMinor = Number(appPaymentCfg?.ticketfee ?? 0) || 0;
    const platformFeeMinor = form.ticketPlatformFeeHandling === 'add' ? configuredTicketFeeMinor : 0;
    const addsStripeToBuyer = form.ticketPlatformFeeHandling === 'add';
    const buyerPaysMinor = addsStripeToBuyer ? grossUpForStripeFeeMinor(unitMinor + platformFeeMinor, stripeCfg) : unitMinor;
    const buyerStripeFeeMinor = addsStripeToBuyer ? Math.max(0, buyerPaysMinor - unitMinor - platformFeeMinor) : stripeFeeMinor;
    const organiserReceivesMinor = addsStripeToBuyer ? unitMinor : Math.max(unitMinor - stripeFeeMinor - platformFeeMinor, 0);
    return {
      currency,
      unitMinor,
      stripeFeeMinor: buyerStripeFeeMinor,
      platformFeeMinor,
      buyerPaysMinor,
      organiserReceivesMinor,
      unit: toMajorFromMinor(unitMinor, currency),
      stripeFee: toMajorFromMinor(buyerStripeFeeMinor, currency),
      platformFee: toMajorFromMinor(platformFeeMinor, currency),
      buyerPays: toMajorFromMinor(buyerPaysMinor, currency),
      organiserReceives: toMajorFromMinor(organiserReceivesMinor, currency),
      stripeCfg,
      addsStripeToBuyer
    };
  }, [form.ticketCurrency, form.ticketPriceMajor, form.ticketPricePence, form.ticketPlatformFeeHandling, appPaymentCfg?.ticketfee, teamFeeCfg]);
  const isTicketed = form.attendanceMode === 'ticketed';
  const isRecurringEvent = useMemo(() => isRecurringRsvpEvent(form), [form]);
  const effectiveRsvpDeadline = useMemo(() => getEffectiveRsvpDeadline(form, {
    occurrenceId: selectedOccurrenceId || form.occurrenceId || ''
  }), [form, selectedOccurrenceId]);
  const rsvpDeadlineClosed = useMemo(() => isRsvpDeadlinePassed(form, {
    occurrenceId: selectedOccurrenceId || form.occurrenceId || ''
  }), [form, selectedOccurrenceId]);
  const rsvpDeadlineText = useMemo(() => formatRsvpDeadline(effectiveRsvpDeadline, {
    includePrefix: false
  }), [effectiveRsvpDeadline]);
  const isOccurrenceScopeEditing = isExisting && isRecurringEvent && editMode && editScopeIntent === 'occurrence' && !!selectedOccurrenceId;
  const isOccurrenceOverrideDoc = !!form.isOccurrenceOverride || form.scope === 'occurrence';
  const occurrenceEventDocId = isOccurrenceScopeEditing ? buildOccurrenceEventDocId(form.id, selectedOccurrenceId) : null;
  const roleEventId = (isOccurrenceOverrideDoc && form.originalEventId && form.occurrenceId ? buildOccurrenceEventDocId(form.originalEventId, form.occurrenceId) : null) || (isOccurrenceScopeEditing && occurrenceEventDocId ? occurrenceEventDocId : null) || form.id;
  const roleCoverageScope = isOccurrenceScopeEditing || isOccurrenceOverrideDoc ? 'event' : isRecurringEvent ? 'series' : 'event';
  const roleCoverageHint = isOccurrenceScopeEditing ? 'Role assignments in this edit apply to this occurrence override only.' : isOccurrenceOverrideDoc ? 'Role assignments and approvals here apply to this occurrence override only.' : isRecurringEvent ? 'Role assignments and approvals on recurring events apply to the entire series.' : 'Role assignments apply to this event only.';
  const allowRoleScopeChoice = roleCoverageScope === 'series' && isRecurringEvent && !!selectedOccurrenceId;
  const selectedOccurrenceRoleEventId = allowRoleScopeChoice ? buildOccurrenceEventDocId(form.id, selectedOccurrenceId) : null;
  const isOccurrenceDeleteIntent = isExisting && isRecurringEvent && editMode && editScopeIntent === 'occurrence' && !!(selectedOccurrenceId || form.occurrenceId);
  const confirmSeriesRoleAction = useCallback(actionLabel => {
    if (roleCoverageScope !== 'series') return true;
    return safeConfirm(`${actionLabel} will apply to the entire series (all occurrences), not only this event. Continue?`);
  }, [roleCoverageScope]);
  const ticketing = useTicketPurchase({
    teamId,
    eventId: form.id || null,
    userId: userId || null,
    checkoutUrl: form.checkoutUrl,
    ticketName: form.ticketName || form.name,
    ticketCurrency: form.ticketCurrency || 'GBP',
    ticketPriceMajor: fallbackTicketMeta.price,
    displayPriceMajor: ticketPricing?.buyerPays,
    ticketPaymentBasis: form.ticketPaymentBasis,
    occurrenceId: selectedOccurrenceId || form.occurrenceId || null,
    isRecurring: isRecurringEvent,
    forceCallableCheckout: form.capacity !== '' && form.capacity != null && Number.isFinite(Number(form.capacity)),
  });
  useEffect(() => {
    setForm(prev => (
      prev._hasPaidAdmission === (ticketing.status === 'paid')
      && prev._hasAdmissionBooking === ticketing.hasCompletedBooking
        ? prev
        : { ...prev, _hasPaidAdmission: ticketing.status === 'paid', _hasAdmissionBooking: ticketing.hasCompletedBooking }
    ));
  }, [ticketing.hasCompletedBooking, ticketing.status]);
  const eventStartTime = useMemo(() => {
    if (isRecurringEvent && form.ticketPaymentBasis === 'whole_series' && form.recurrence?.endDate) {
      const end = new Date(`${form.recurrence.endDate}T${form.time || form.recurrence?.time || '23:59'}`);
      if (!Number.isNaN(end.getTime())) return new Date(end.getTime() + Math.max(1, Number(form.durationMinutes || 60)) * 60000);
    }
    const activeOccurrenceId = selectedOccurrenceId || form.occurrenceId;
    if (isRecurringEvent && activeOccurrenceId) {
      const d = new Date(`${activeOccurrenceId}T${form.time || form.recurrence?.time || '00:00'}`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (form.start?.toDate) return form.start.toDate();
    if (form.date && form.time) {
      const d = new Date(`${form.date}T${form.time}`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
  }, [form.start, form.date, form.time, form.recurrence, form.occurrenceId, form.durationMinutes, form.ticketPaymentBasis, isRecurringEvent, selectedOccurrenceId]);
  const checkoutUnavailableMessage = useMemo(() => {
    if (!isRecurringEvent || form.ticketPaymentBasis !== 'per_occurrence') return '';
    const occurrenceDate = selectedOccurrenceId || form.occurrenceId;
    if (!occurrenceDate) return 'Choose an occurrence to buy a ticket.';
    const start = new Date(`${occurrenceDate}T${form.time || form.recurrence?.time || '00:00'}`);
    if (Number.isNaN(start.getTime())) return 'This occurrence is not available for checkout.';
    const opens = new Date(start.getTime() - Math.max(0, Number(form.ticketSaleOpenDaysBefore ?? 30)) * 86400000);
    const closes = new Date(start.getTime() - Math.max(0, Number(form.ticketSaleCloseHoursBefore ?? 0)) * 3600000);
    if (Date.now() < opens.getTime()) return `Ticket sales open ${opens.toLocaleString()}.`;
    if (Date.now() > closes.getTime()) return 'Ticket sales have closed for this occurrence.';
    return '';
  }, [form.occurrenceId, form.recurrence, form.ticketPaymentBasis, form.ticketSaleCloseHoursBefore, form.ticketSaleOpenDaysBefore, form.time, isRecurringEvent, selectedOccurrenceId]);
  const adminPayoutTimingText = useMemo(() => {
    if (!isAdmin && !canManageEventRefunds) return '';
    return formatEventPayoutTiming(form, selectedOccurrenceId || form.occurrenceId || '');
  }, [canManageEventRefunds, form, isAdmin, selectedOccurrenceId]);
  useEffect(() => {
    const check = async () => {
      if (!teamId || !form.id || !userId) return;
      try {
        const ticketsRef = collection(db, 'teams', teamId, 'events', form.id, 'tickets');
        const snap = await getDocs(ticketsRef);
        for (const t of snap.docs) {
          const data = t.data();
          if ((data.userId || data.memberId) !== userId) continue;
          const applies = ticketAppliesToEvent(data, {
            isRecurring: isRecurringEvent,
            ticketPaymentBasis: form.ticketPaymentBasis,
            occurrenceId: selectedOccurrenceId || form.occurrenceId,
          });
          if (!applies) continue;
          const reqRef = doc(db, 'teams', teamId, 'events', form.id, 'refundRequests', `${t.id}:${userId}`);
          const reqSnap = await getDoc(reqRef);
          if (reqSnap.exists() && reqSnap.data()?.status === 'requested') {
            setRefundRequested(true);
            return;
          }
        }
        setRefundRequested(false);
      } catch (_) {
        setRefundRequested(false);
      }
    };
    check();
  }, [teamId, form.id, form.occurrenceId, form.ticketPaymentBasis, isRecurringEvent, selectedOccurrenceId, userId, isOpen]);
  const canViewAttendees = useMemo(() => {
    if (!isExisting || !form.id) return false;
    if (form.attendanceMode !== 'rsvp' && form.attendanceMode !== 'ticketed') return false;
    if (isAdmin || canManageEventRefunds) return true;
    if (form.attendanceMode === 'rsvp') {
      return myRsvpStatus === 'yes';
    }
    // Paid-event attendee rosters are administrative; purchasers see only
    // their own booking and admission passes.
    return false;
  }, [canManageEventRefunds, form.attendanceMode, form.id, isAdmin, isExisting, myRsvpStatus]);
  useEffect(() => {
    if (!isOpen || !teamId || !userId) {
      setCurrentUserName('');
      setMemberTagNames([]);
      setIsTeamMember(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const [memberSnap, userSnap] = await Promise.all([getDoc(doc(db, 'teams', teamId, 'members', userId)), getDoc(doc(db, 'users', userId))]);
        if (cancelled) return;
        setIsTeamMember(memberSnap.exists());
        setMemberTagNames(normalizeMemberTags(memberSnap.data()?.tags));
        if (userSnap.exists()) {
          setCurrentUserName(displayNameFromUserDoc(userId, userSnap.data()));
        } else {
          setCurrentUserName('Member');
        }
      } catch (err) {
        if (!cancelled) {
          setIsTeamMember(false);
          setMemberTagNames([]);
          setCurrentUserName('Member');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, teamId, userId]);
  useEffect(() => {
    if (!isOpen || !teamId || !roleEventId) {
      setRoleRequests([]);
      return;
    }
    const unsub = safeOnSnapshot(collection(db, 'teams', teamId, 'events', roleEventId, 'roleRequests'), snap => {
      setRoleRequests(snap.docs.map(d => ({
        id: d.id,
        eventDocId: roleEventId,
        ...d.data()
      })));
    }, undefined, { label: 'event-role-requests', teamId, eventId: roleEventId });
    return () => unsub();
  }, [isOpen, teamId, roleEventId]);
  useEffect(() => {
    if (!isOpen || !teamId || !selectedOccurrenceRoleEventId || selectedOccurrenceRoleEventId === roleEventId) {
      setOccurrenceRoleRequests([]);
      return;
    }
    const unsub = safeOnSnapshot(collection(db, 'teams', teamId, 'events', selectedOccurrenceRoleEventId, 'roleRequests'), snap => {
      setOccurrenceRoleRequests(snap.docs.map(d => ({
        id: d.id,
        eventDocId: selectedOccurrenceRoleEventId,
        ...d.data()
      })));
    }, undefined, { label: 'event-occurrence-role-requests', teamId, eventId: selectedOccurrenceRoleEventId });
    return () => unsub();
  }, [isOpen, teamId, roleEventId, selectedOccurrenceRoleEventId]);
  useEffect(() => {
    if (!isOpen || !isAdmin || !teamId) {
      setTeamMembers([]);
      return;
    }
    let cancelled = false;
    const loadMembers = async () => {
      try {
        const membersSnap = await getDocs(collection(db, 'teams', teamId, 'members'));
        const ids = membersSnap.docs.map(d => d.id);
        const mapped = await Promise.all(ids.map(async uid => {
          try {
            const userSnap = await getDoc(doc(db, 'users', uid));
            const data = userSnap.exists() ? userSnap.data() : {};
            const firstName = typeof data?.firstName === 'string' ? data.firstName.trim() : '';
            const lastName = typeof data?.lastName === 'string' ? data.lastName.trim() : '';
            const username = typeof data?.username === 'string' ? data.username.trim() : '';
            return {
              id: uid,
              name: displayNameFromUserDoc(uid, data),
              firstName,
              lastName,
              username
            };
          } catch {
            return {
              id: uid,
              name: 'Member',
              firstName: '',
              lastName: '',
              username: ''
            };
          }
        }));
        if (!cancelled) {
          setTeamMembers(mapped.sort((a, b) => a.name.localeCompare(b.name)));
        }
      } catch (err) {
        if (!cancelled) setTeamMembers([]);
      }
    };
    loadMembers();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isAdmin, teamId]);
  const myRequestsBySlot = useMemo(() => {
    const map = {};
    if (!userId) return map;
    roleRequests.filter(req => req.requestedBy === userId).forEach(req => {
      if (!req.slotId) return;
      map[req.slotId] = req;
    });
    return map;
  }, [roleRequests, userId]);
  const myRequestsByScopeSlot = useMemo(() => {
    const map = {};
    if (!userId) return map;
    [...roleRequests, ...occurrenceRoleRequests].filter(req => req.requestedBy === userId).forEach(req => {
      if (!req.slotId) return;
      const inferredScope = req.scope === 'occurrence' || (selectedOccurrenceRoleEventId && req.eventDocId === selectedOccurrenceRoleEventId) ? 'occurrence' : 'series';
      map[`${inferredScope}:${req.slotId}`] = req;
    });
    return map;
  }, [occurrenceRoleRequests, roleRequests, selectedOccurrenceRoleEventId, userId]);
  const pendingRoleRequests = useMemo(() => [...roleRequests, ...occurrenceRoleRequests].filter(req => (req.status || 'requested') === 'requested'), [occurrenceRoleRequests, roleRequests]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name === 'waitlistMode' && value === 'off' && form.waitlistMode !== 'off' && isExisting && !safeConfirm('Close this waitlist? Queued requests and unclaimed offers will be cancelled.')) return;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleTagToggle = name => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.includes(name) ? prev.tags.filter(t => t !== name) : [...prev.tags, name]
    }));
  };
  const addRoleFromPreset = presetId => {
    const preset = (eventRoles || []).find(role => role.id === presetId);
    if (!preset) return;
    const count = Math.max(1, Number(preset.defaultSlots) || 1);
    const newRole = {
      id: crypto.randomUUID(),
      name: preset.name || 'Role',
      requiresApproval: preset.requiresApproval !== false,
      allowedTags: Array.isArray(preset.allowedTags) ? preset.allowedTags : [],
      slots: Array.from({
        length: count
      }, () => createVacantSlot())
    };
    setForm(prev => ({
      ...prev,
      roles: [...normalizeEventRoles(prev.roles), newRole]
    }));
  };
  const quickAddRole = async ({
    name,
    slots,
    allowedTags,
    requiresApproval,
    saveAsPreset
  }) => {
    const cleanName = String(name || '').trim();
    if (!cleanName) return;
    const slotCount = Math.max(1, Number(slots) || 1);
    const nextRole = {
      id: crypto.randomUUID(),
      name: cleanName,
      allowedTags: Array.isArray(allowedTags) ? allowedTags : [],
      requiresApproval: requiresApproval !== false,
      slots: Array.from({
        length: slotCount
      }, () => createVacantSlot())
    };
    setForm(prev => ({
      ...prev,
      roles: [...normalizeEventRoles(prev.roles), nextRole]
    }));
    if (saveAsPreset && isAdmin && teamId) {
      await setDoc(doc(collection(db, 'teams', teamId, 'eventRoles')), {
        name: cleanName,
        defaultSlots: slotCount,
        allowedTags: Array.isArray(allowedTags) ? allowedTags : [],
        requiresApproval: requiresApproval !== false,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId || null,
        updatedBy: userId || null
      });
    }
  };
  const updateRole = (index, patch) => {
    setForm(prev => {
      const roles = normalizeEventRoles(prev.roles);
      return {
        ...prev,
        roles: roles.map((role, i) => i === index ? {
          ...role,
          ...patch
        } : role)
      };
    });
  };
  const resizeRole = (index, nextCount) => {
    try {
      setForm(prev => {
        const roles = normalizeEventRoles(prev.roles);
        return {
          ...prev,
          roles: roles.map((role, i) => i === index ? {
            ...role,
            slots: resizeRoleSlots(role, nextCount)
          } : role)
        };
      });
    } catch (err) {
      alert(err?.message || 'Unable to change slot count.');
    }
  };
  const toggleRoleAllowedTag = (index, tagName) => {
    setForm(prev => {
      const roles = normalizeEventRoles(prev.roles);
      return {
        ...prev,
        roles: roles.map((role, i) => {
          if (i !== index) return role;
          const next = new Set(role.allowedTags || []);
          if (next.has(tagName)) next.delete(tagName);else next.add(tagName);
          return {
            ...role,
            allowedTags: Array.from(next)
          };
        })
      };
    });
  };
  const removeRole = index => {
    setForm(prev => {
      const roles = normalizeEventRoles(prev.roles);
      return {
        ...prev,
        roles: roles.filter((_, i) => i !== index)
      };
    });
  };
  const assignRoleInForm = ({
    roleIndex,
    slotId,
    memberId
  }) => {
    const memberName = teamMembers.find(member => member.id === memberId)?.name || 'Member';
    setForm(prev => {
      const roles = normalizeEventRoles(prev.roles);
      return {
        ...prev,
        roles: roles.map((role, i) => {
          if (i !== roleIndex) return role;
          return {
            ...role,
            slots: (role.slots || []).map(slot => slot.id === slotId ? {
              ...slot,
              assigneeUserId: memberId,
              assigneeDisplayName: memberName
            } : slot)
          };
        })
      };
    });
  };
  const clearRoleInForm = ({
    roleIndex,
    slotId
  }) => {
    setForm(prev => {
      const roles = normalizeEventRoles(prev.roles);
      return {
        ...prev,
        roles: roles.map((role, i) => {
          if (i !== roleIndex) return role;
          return {
            ...role,
            slots: (role.slots || []).map(slot => slot.id === slotId ? {
              ...slot,
              assigneeUserId: null,
              assigneeDisplayName: ''
            } : slot)
          };
        })
      };
    });
  };
  const handleSave = async (e, stripeConfirmed = false) => {
    e.preventDefault();
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(form.time || '')) {
      setSaveError('Choose an event time before saving.');
      return;
    }
    const preflightPence = normaliseMajorIntoPence(form.ticketPriceMajor, form.ticketCurrency);
    const preflightPrice = typeof preflightPence === 'number'
      ? preflightPence
      : typeof form.ticketPricePence === 'number' ? form.ticketPricePence : 0;
    if (form.attendanceMode === 'ticketed' && Number(preflightPrice || 0) > 0 && !stripeConfirmed) {
      await requireStripePayment({
        feature: 'paid_event',
        featureLabel: 'Paid events',
        returnTarget: 'paid_event',
        onReady: () => handleSave({ preventDefault() {} }, true),
      });
      return;
    }
    if (form.allowNonMembers) {
      if (!['individual_accounts', 'buyer_managed_guests'].includes(form.externalAttendeeMode)) {
        setSaveError('Choose how external attendees will be registered.');
        return;
      }
      if (!['basic', 'safety'].includes(form.externalInfoPreset)) {
        setSaveError('Choose the information required for external attendees.');
        return;
      }
      if (form.eventMode !== 'online' && (!String(form.locationCity || '').trim() || !String(form.locationCountry || '').trim())) {
        setSaveError('Add a city and country for the public location preview.');
        return;
      }
    }
    setSaveError('');
    setIsSaving(true);
    let eventPersisted = false;
    let persistedEventId = form.id || '';
    let savePhase = 'event details';
    try {
      if (form.attendanceMode === 'ticketed' && form.type === 'recurring') {
        if (!['per_occurrence', 'whole_series'].includes(form.ticketPaymentBasis)) {
          throw new Error('Choose whether members pay for each occurrence or the whole series.');
        }
        if (form.ticketPaymentBasis === 'whole_series' && !form.recurrenceEndDate) {
          throw new Error('Whole-series paid events need an end date.');
        }
        if (form.checkoutUrl) {
          throw new Error('Static checkout links cannot be used for recurring paid events.');
        }
        const openMinutes = Number(form.ticketSaleOpenDaysBefore) * 24 * 60;
        const closeMinutes = Number(form.ticketSaleCloseHoursBefore) * 60;
        if (form.ticketPaymentBasis === 'per_occurrence' && (!Number.isFinite(openMinutes) || !Number.isFinite(closeMinutes) || openMinutes < closeMinutes)) {
          throw new Error('Sales must open before they close for each occurrence.');
        }
      }
      if (form.waitlistMode !== 'off') {
        const claimHours = Number(form.waitlistClaimWindowHours);
        if (!Number.isInteger(claimHours) || claimHours < 1 || claimHours > 168) throw new Error('Waitlist claim time must be a whole number from 1 to 168 hours.');
        if (!Number.isFinite(Number(form.capacity)) || Number(form.capacity) < 1) throw new Error('Set a capacity before enabling the waitlist.');
      }
      if (hasPaidTickets && initialData) {
        if (
          (initialData.externalAttendeeMode || 'individual_accounts') !== form.externalAttendeeMode
          || (initialData.externalInfoPreset || 'safety') !== form.externalInfoPreset
        ) {
          throw new Error('External attendee settings are locked because tickets have been sold.');
        }
        const originalType = inferEventTypeFromData(initialData);
        const originalBasis = initialData.ticketPaymentBasis || (originalType === 'recurring' ? '' : null);
        const originalEnd = initialData.recurrence?.endDate || initialData.recurrenceEndDate || '';
        const scheduleChanged = originalType !== form.type
          || originalBasis !== form.ticketPaymentBasis
          || String(initialData.recurrence?.frequency || initialData.frequency || 'WEEKLY') !== String(form.frequency || 'WEEKLY')
          || String(initialData.recurrence?.byDay?.[0] || '') !== String(form.dayOfWeek || '').slice(0, 2).toUpperCase()
          || String(initialData.recurrence?.weekOfMonth || '') !== String(form.frequency === 'MONTHLY' ? form.weekOfMonth || '' : '')
          || Number(initialData.recurrence?.interval || 1) !== Number(form.recurrenceInterval || 1)
          || String(initialData.recurrence?.startDate || '') !== String(form.recurrenceStartDate || '')
          || String(originalEnd) !== String(form.recurrenceEndDate || '');
        if (scheduleChanged) throw new Error('The payment basis and recurrence schedule are locked because tickets have been sold.');
      }
      const enablingExistingAdmissions = isExisting
        && initialData?.admissionTicketsEnabled !== true
        && form.attendanceMode === 'ticketed'
        && form.admissionTicketsEnabled === true;
      if (enablingExistingAdmissions && !safeConfirm('Enable scannable admission tickets for this event? Active future bookings will receive passes and this cannot be switched off after passes are issued.')) {
        setIsSaving(false);
        return;
      }
      const id = form.id || crypto.randomUUID();
      persistedEventId = id;
      const ref = doc(db, 'teams', teamId, 'events', id);
      const nextCapacity = normalizeOptionalEventCapacity(form.capacity);
      if (isExisting && nextCapacity != null && (!isRecurringEvent || form.ticketPaymentBasis === 'whole_series' || selectedOccurrenceId)) {
        const getWaitlist = httpsCallable(functions, 'getEventWaitlist');
        const result = await getWaitlist({ teamId, eventId: id, ...(isRecurringEvent && form.ticketPaymentBasis !== 'whole_series' ? { occurrenceId: selectedOccurrenceId } : {}) });
        const occupiedAndReserved = Number(result.data?.confirmedCount || 0) + Number(result.data?.reservedCount || 0);
        if (nextCapacity < occupiedAndReserved) throw new Error(`Capacity cannot be lower than the ${occupiedAndReserved} confirmed or reserved places.`);
      }
      const pence = normaliseMajorIntoPence(form.ticketPriceMajor, form.ticketCurrency);
      const ticketPricePence = typeof pence === 'number' ? pence : typeof form.ticketPricePence === 'number' ? form.ticketPricePence : undefined;
      const ticketPriceMajor = keepIfNonEmpty(form.ticketPriceMajor) ?? (typeof ticketPricePence === 'number' ? normalisePriceIntoMajor(ticketPricePence, form.ticketCurrency) : undefined);
    if (!form.locationId && form.eventMode === 'inPerson' && form.saveLocation) {
      const locRef = doc(db, 'teams', teamId, 'locations', crypto.randomUUID());
      await setDoc(locRef, removeUndefinedFields({
        name: keepIfNonEmpty(form.locationName),
        street: keepIfNonEmpty(form.locationStreet),
        city: keepIfNonEmpty(form.locationCity),
        postcode: keepIfNonEmpty(form.locationPostcode),
        country: keepIfNonEmpty(form.locationCountry) || teamCountry || 'UK',
        createdAt: serverTimestamp()
      }));
    }
    if (form.type === 'recurring' && Number(form.recurrenceInterval) > 1 && !parseDateIsoLocal(form.recurrenceStartDate)) throw new Error('Choose a start date for the repeating schedule.');
    if (form.type === 'recurring' && form.recurrenceStartDate && form.recurrenceEndDate && form.recurrenceEndDate < form.recurrenceStartDate) throw new Error('The end date must be on or after the start date.');
    const isOneOff = (form.type || 'oneOff') === 'oneOff';
    const recurrence = !isOneOff ? {
      frequency: keepIfNonEmpty(form.frequency) || 'WEEKLY',
      byDay: [keepIfNonEmpty(form.dayOfWeek) ? form.dayOfWeek.slice(0, 2).toUpperCase() : 'WE'],
      time: form.time,
      weekOfMonth: (keepIfNonEmpty(form.frequency) || form.frequency) === 'MONTHLY' ? keepIfNonEmpty(form.weekOfMonth) || 'First' : null,
      interval: Math.max(1, Math.floor(Number(form.recurrenceInterval) || 1)),
      startDate: keepIfNonEmpty(form.recurrenceStartDate) || null,
      endDate: keepIfNonEmpty(form.recurrenceEndDate) || null
    } : null;
    const usesRsvp = form.attendanceMode === 'rsvp';
    const deadlineUsesOffset = isRecurringEvent || !isOneOff;
    let rsvpDeadlinePayload = deleteField();
    let rsvpReminderPayload = deleteField();
    if (usesRsvp && form.rsvpDeadlineEnabled) {
      const deadlineOffsetMinutes = Number(form.rsvpDeadlineOffsetMinutes) || DEFAULT_RSVP_DEADLINE_OFFSET_MINUTES;
      if (deadlineUsesOffset) {
        rsvpDeadlinePayload = {
          enabled: true,
          offsetMinutesBeforeStart: deadlineOffsetMinutes
        };
      } else {
        const eventStart = toStartEnd(form.date, form.time, form.durationMinutes).start;
        if (!eventStart) {
          throw new Error('Choose a valid event date and time before setting an RSVP deadline.');
        }
        rsvpDeadlinePayload = {
          enabled: true,
          deadlineAt: new Date(eventStart.getTime() - deadlineOffsetMinutes * 60 * 1000)
        };
      }
      if (form.rsvpReminderEnabled) {
        const reminderOffsets = normalizeRsvpReminderOffsets(form.rsvpReminderOffsetMinutesList || [form.rsvpReminderOffsetMinutes]);
        const deadlineForValidation = getConcreteDeadlineForReminderValidation({
          form,
          deadlineUsesOffset,
          deadlineOffsetMinutes,
          deadlineAt: rsvpDeadlinePayload.deadlineAt,
          isRecurringEvent,
          selectedOccurrenceId
        });
        const shouldBlockPastReminder = !isRecurringEvent || isOccurrenceScopeEditing || isOccurrenceOverrideDoc;
        const pastReminderAt = shouldBlockPastReminder && deadlineForValidation
          ? reminderOffsets
            .map((offset) => getRsvpReminderSendAt(deadlineForValidation, offset))
            .find((reminderSendAt) => reminderSendAt && reminderSendAt.getTime() <= Date.now())
          : null;
        if (pastReminderAt) {
          const reminderTime = formatRsvpDeadline(pastReminderAt, { includePrefix: false });
          alert(`An RSVP reminder time has already passed${reminderTime ? ` (${reminderTime})` : ''}. Choose a shorter reminder period, move the RSVP deadline later, or remove that reminder.`);
          return;
        }
        rsvpReminderPayload = {
          enabled: true,
          offsetMinutesBeforeDeadlineList: reminderOffsets,
          offsetMinutesBeforeDeadline: reminderOffsets[reminderOffsets.length - 1]
        };
      } else {
        rsvpReminderPayload = {
          enabled: false
        };
      }
    }
    const nextRsvpDeadlineState = rsvpDeadlinePayload?.enabled === true ? rsvpDeadlinePayload : null;
    const nextRsvpReminderState = rsvpReminderPayload?.enabled === true || rsvpReminderPayload?.enabled === false ? rsvpReminderPayload : null;
    const nextRoles = normalizeEventRoles(form.roles);
    if (isOccurrenceScopeEditing && occurrenceEventDocId) {
      const occurrenceTime = keepIfNonEmpty(form.time) || '00:00';
      const {
        start: occurrenceStart,
        end: occurrenceEnd
      } = toStartEnd(selectedOccurrenceId, occurrenceTime, form.durationMinutes);
      const occurrencePayload = {
        id: occurrenceEventDocId,
        originalEventId: id,
        eventId: occurrenceEventDocId,
        scope: 'occurrence',
        occurrenceId: selectedOccurrenceId,
        name: keepIfNonEmpty(form.name),
        type: 'oneOff',
        date: selectedOccurrenceId,
        time: occurrenceTime,
        durationMinutes: Number(form.durationMinutes || 60),
        tags: Array.isArray(form.tags) ? form.tags : [],
        eventMode: form.eventMode || 'inPerson',
        onlineUrl: keepIfNonEmpty(form.onlineUrl),
        locationName: keepIfNonEmpty(form.locationName),
        locationStreet: keepIfNonEmpty(form.locationStreet),
        locationCity: keepIfNonEmpty(form.locationCity),
        locationPostcode: keepIfNonEmpty(form.locationPostcode),
        locationCountry: keepIfNonEmpty(form.locationCountry),
        attendanceMode: form.attendanceMode || 'none',
        capacity: nextCapacity,
        waitlist: { mode: form.waitlistMode || 'off', claimWindowHours: Number(form.waitlistClaimWindowHours || 24) },
        rsvpDeadline: rsvpDeadlinePayload,
        rsvpReminder: rsvpReminderPayload,
        allowNonMembers: !!form.allowNonMembers,
        ticketName: keepIfNonEmpty(form.ticketName),
        ticketCurrency: form.ticketCurrency || 'GBP',
        ticketTypes: Array.isArray(form.ticketTypes) ? form.ticketTypes : [],
        extras: Array.isArray(form.extras) ? form.extras : [],
        bookingForm: form.bookingForm || null,
        protectedAnswerRetentionDays: Math.max(1, Number(form.protectedAnswerRetentionDays || 30)),
        ...(ticketPriceMajor !== undefined && {
          ticketPriceMajor
        }),
        ...(ticketPricePence !== undefined && {
          ticketPricePence
        }),
        controlSalesTime: false,
        ticketSaleStart: deleteField(),
        ticketSaleEnd: deleteField(),
        ticketPlatformFeeHandling: form.ticketPlatformFeeHandling || 'add',
        ticketPaymentBasis: keepIfNonEmpty(form.ticketPaymentBasis),
        ticketSaleOpenDaysBefore: Number(form.ticketSaleOpenDaysBefore ?? 30),
        ticketSaleCloseHoursBefore: Number(form.ticketSaleCloseHoursBefore ?? 0),
        stripeProductId: keepIfNonEmpty(form.stripeProductId),
        stripePriceId: keepIfNonEmpty(form.stripePriceId),
        checkoutUrl: keepIfNonEmpty(form.checkoutUrl),
        roles: nextRoles,
        start: occurrenceStart || deleteField(),
        end: occurrenceEnd || deleteField(),
        recurrence: deleteField(),
        frequency: deleteField(),
        dayOfWeek: deleteField(),
        weekOfMonth: deleteField(),
        updatedAt: serverTimestamp()
      };
      const cleanedOccurrencePayload = removeUndefinedFields(occurrencePayload);
      await setDoc(doc(db, 'teams', teamId, 'events', occurrenceEventDocId), cleanedOccurrencePayload, {
        merge: true
      });
      eventPersisted = true;
      savePhase = 'series waitlist settings';
      await setDoc(ref, { waitlist: occurrencePayload.waitlist, updatedAt: serverTimestamp() }, { merge: true });
      const previousWaitlistMode = ['manual', 'automatic'].includes(initialData?.waitlist?.mode) ? initialData.waitlist.mode : 'off';
      if (previousWaitlistMode !== 'off' && form.waitlistMode === 'off') {
        savePhase = 'closing the previous waitlist';
        await httpsCallable(functions, 'closeEventWaitlist')({ teamId, eventId: id });
      } else if (form.waitlistMode === 'automatic') {
        savePhase = 'automatic waitlist activation';
        await httpsCallable(functions, 'reconcileEventWaitlist')({ teamId, eventId: id, occurrenceId: selectedOccurrenceId });
      }
      setSavedRolesSnapshot(nextRoles);
      setForm(prev => ({
        ...prev,
        rsvpDeadline: nextRsvpDeadlineState || undefined,
        rsvpReminder: nextRsvpReminderState || undefined
      }));
      setEditMode(false);
      return;
    }
    const oneOffDate = isOneOff ? keepIfNonEmpty(form.date) : null;
    const oneOffTime = keepIfNonEmpty(form.time);
    const {
      start: oneOffStart,
      end: oneOffEnd
    } = isOneOff ? toStartEnd(oneOffDate, oneOffTime, form.durationMinutes) : {
      start: null,
      end: null
    };
    const payload = {
      id,
      name: keepIfNonEmpty(form.name),
      type: form.type || 'oneOff',
      date: isOneOff ? oneOffDate : deleteField(),
      time: oneOffTime,
      durationMinutes: Number(form.durationMinutes || 60),
      frequency: isOneOff ? deleteField() : keepIfNonEmpty(form.frequency),
      dayOfWeek: isOneOff ? deleteField() : keepIfNonEmpty(form.dayOfWeek),
      weekOfMonth: isOneOff ? deleteField() : keepIfNonEmpty(form.weekOfMonth),
      recurrence: isOneOff ? deleteField() : recurrence,
      start: isOneOff ? oneOffStart || deleteField() : deleteField(),
      end: isOneOff ? oneOffEnd || deleteField() : deleteField(),
      tags: Array.isArray(form.tags) ? form.tags : [],
      eventMode: form.eventMode || 'inPerson',
      onlineUrl: keepIfNonEmpty(form.onlineUrl),
      locationName: keepIfNonEmpty(form.locationName),
      locationStreet: keepIfNonEmpty(form.locationStreet),
      locationCity: keepIfNonEmpty(form.locationCity),
      locationPostcode: keepIfNonEmpty(form.locationPostcode),
      locationCountry: keepIfNonEmpty(form.locationCountry),
      attendanceMode: form.attendanceMode || 'none',
      capacity: nextCapacity,
      waitlist: { mode: form.waitlistMode || 'off', claimWindowHours: Number(form.waitlistClaimWindowHours || 24) },
      rsvpDeadline: rsvpDeadlinePayload,
      rsvpReminder: rsvpReminderPayload,
      allowNonMembers: !!form.allowNonMembers,
      externalAttendeeMode: form.externalAttendeeMode || 'individual_accounts',
      externalInfoPreset: form.externalInfoPreset || 'safety',
      admissionTicketsEnabled: form.attendanceMode === 'ticketed' ? form.admissionTicketsEnabled === true : false,
      publicDescription: keepIfNonEmpty(form.publicDescription),
      ticketName: keepIfNonEmpty(form.ticketName),
      ticketCurrency: form.ticketCurrency || 'GBP',
      ticketTypes: Array.isArray(form.ticketTypes) ? form.ticketTypes : [],
      extras: Array.isArray(form.extras) ? form.extras : [],
      bookingForm: form.bookingForm || null,
      protectedAnswerRetentionDays: Math.max(1, Number(form.protectedAnswerRetentionDays || 30)),
      ...(ticketPriceMajor !== undefined && {
        ticketPriceMajor
      }),
      ...(ticketPricePence !== undefined && {
        ticketPricePence
      }),
      controlSalesTime: isOneOff ? !!form.controlSalesTime : false,
      ticketSaleStart: isOneOff ? keepIfNonEmpty(form.ticketSaleStart) : deleteField(),
      ticketSaleEnd: isOneOff ? keepIfNonEmpty(form.ticketSaleEnd) : deleteField(),
      ticketPlatformFeeHandling: form.ticketPlatformFeeHandling || 'add',
      ticketPaymentBasis: isOneOff ? deleteField() : keepIfNonEmpty(form.ticketPaymentBasis),
      ticketSaleOpenDaysBefore: !isOneOff && form.ticketPaymentBasis === 'per_occurrence' ? Number(form.ticketSaleOpenDaysBefore ?? 30) : deleteField(),
      ticketSaleCloseHoursBefore: !isOneOff && form.ticketPaymentBasis === 'per_occurrence' ? Number(form.ticketSaleCloseHoursBefore ?? 0) : deleteField(),
      stripeProductId: keepIfNonEmpty(form.stripeProductId),
      stripePriceId: keepIfNonEmpty(form.stripePriceId),
      checkoutUrl: keepIfNonEmpty(form.checkoutUrl),
      roles: nextRoles,
      updatedAt: serverTimestamp()
    };
    const cleanedPayload = removeUndefinedFields(payload);
    await setDoc(ref, cleanedPayload, {
      merge: true
    });
    eventPersisted = true;
    if (isExisting && form.attendanceMode === 'ticketed' && initialData?.admissionTicketsEnabled !== form.admissionTicketsEnabled) {
      const configureAdmissions = httpsCallable(functions, 'configureEventAdmissionTickets');
      await configureAdmissions({ teamId, eventId: id, enabled: form.admissionTicketsEnabled === true });
    }
    const previousWaitlistMode = ['manual', 'automatic'].includes(initialData?.waitlist?.mode) ? initialData.waitlist.mode : 'off';
    if (previousWaitlistMode !== 'off' && form.waitlistMode === 'off') {
      savePhase = 'closing the previous waitlist';
      const closeWaitlist = httpsCallable(functions, 'closeEventWaitlist');
      await closeWaitlist({ teamId, eventId: id });
    } else if (isExisting && form.waitlistMode === 'automatic' && (!isRecurringEvent || form.ticketPaymentBasis === 'whole_series' || selectedOccurrenceId)) {
      savePhase = 'automatic waitlist activation';
      const reconcileWaitlist = httpsCallable(functions, 'reconcileEventWaitlist');
      await reconcileWaitlist({ teamId, eventId: id, ...(isRecurringEvent && form.ticketPaymentBasis !== 'whole_series' ? { occurrenceId: selectedOccurrenceId } : {}) });
    }
    if (form.allowNonMembers) {
      savePhase = 'creating the public event link';
      const ensureSlugs = httpsCallable(functions, 'ensurePublicEventSlugs');
      const slugResult = await ensureSlugs({ teamId, eventId: id });
      setForm(prev => ({
        ...prev,
        publicEventsSlug: slugResult.data?.clubSlug || prev.publicEventsSlug,
        publicSlug: slugResult.data?.eventSlug || prev.publicSlug,
      }));
    }
    try {
      await notifyEligibleMembersForNewVacancies({
        eventId: id,
        eventName: keepIfNonEmpty(form.name) || 'an event',
        previousRoles: savedRolesSnapshot,
        nextRoles
      });
    } catch (err) {}
    setForm(prev => ({
      ...prev,
      id,
      rsvpDeadline: nextRsvpDeadlineState || undefined,
      rsvpReminder: nextRsvpReminderState || undefined
    }));
    setSavedRolesSnapshot(nextRoles);
    setEditMode(false);
    } catch (err) {
      if (eventPersisted || !isActionableEventSaveFailure(err)) {
        reportCaughtError(err, {
          source: 'events',
          severity: eventPersisted ? 'warning' : 'error',
          context: {
            phase: eventPersisted ? `post_save:${savePhase}` : 'save',
            teamId,
            eventId: form.id || '',
            eventMode: form.eventMode || '',
            savingLocation: !form.locationId && form.eventMode === 'inPerson' && form.saveLocation,
          },
        });
      }
      if (eventPersisted) {
        setForm(prev => ({ ...prev, id: persistedEventId }));
        setEditMode(false);
        if (typeof window !== 'undefined') window.alert(eventPostSaveWarning(savePhase, err));
      } else {
        setSaveError(eventSaveFailureMessage(err));
      }
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!isExisting || !form.id) return;
    const seriesEventId = form.originalEventId || form.id;
    const occurrenceIdToDelete = selectedOccurrenceId || form.occurrenceId || null;
    if (isOccurrenceDeleteIntent) {
      if (!occurrenceIdToDelete) {
        alert('Select an occurrence first.');
        return;
      }
      if (!safeConfirm('Delete this occurrence from the series? This only affects this date.')) return;
      try {
        await cancelEventScopeCallable({
          teamId,
          eventId: seriesEventId,
          scope: 'occurrence',
          occurrenceId: occurrenceIdToDelete,
        });
        onClose();
      } catch (err) {
        const message = typeof err?.message === 'string' && err.message.includes('Refund or resolve all active tickets')
          ? err.message
          : 'This occurrence could not be cancelled. Please try again.';
        alert(message);
      }
      return;
    }
    if (hasPaidTickets) return;
    if (isRecurringEvent) {
      if (!safeConfirm('Delete this entire series and all single-occurrence overrides?')) return;
      const eventsSnap = await getDocs(collection(db, 'teams', teamId, 'events'));
      const refsToDelete = [];
      eventsSnap.forEach(eventDoc => {
        const data = eventDoc.data() || {};
        const docId = eventDoc.id;
        const matchesSeries = docId === seriesEventId;
        const matchesOverride = (typeof data.originalEventId === 'string' && data.originalEventId === seriesEventId) || docId.startsWith(`${seriesEventId}__`);
        if (matchesSeries || matchesOverride) {
          refsToDelete.push(eventDoc.ref);
        }
      });
      if (refsToDelete.length > 0) {
        for (let i = 0; i < refsToDelete.length; i += 400) {
          const batch = writeBatch(db);
          refsToDelete.slice(i, i + 400).forEach(ref => batch.delete(ref));
          await batch.commit();
        }
      }
      onClose();
      return;
    }
    if (!safeConfirm(`Delete "${form.name || 'this event'}"? This cannot be undone.`)) return;
    await deleteDoc(doc(db, 'teams', teamId, 'events', form.id));
    onClose();
  };
  const ensureOccurrenceRoleEventDoc = useCallback(async (occurrenceIdOverride = null) => {
    const targetOccurrenceId = typeof occurrenceIdOverride === 'string' && occurrenceIdOverride.trim() !== '' ? occurrenceIdOverride.trim() : selectedOccurrenceId;
    if (!teamId || !form.id || !targetOccurrenceId) {
      throw new Error('Select an occurrence first.');
    }
    const occurrenceDocId = buildOccurrenceEventDocId(form.id, targetOccurrenceId);
    if (!occurrenceDocId) throw new Error('Unable to resolve occurrence id.');
    const occurrenceRef = doc(db, 'teams', teamId, 'events', occurrenceDocId);
    const occurrenceSnap = await getDoc(occurrenceRef);
    if (occurrenceSnap.exists()) return occurrenceDocId;
    const seriesRef = doc(db, 'teams', teamId, 'events', form.id);
    const seriesSnap = await getDoc(seriesRef);
    if (!seriesSnap.exists()) throw new Error('Event no longer exists.');
    const seriesData = seriesSnap.data() || {};
    const occurrenceTime = keepIfNonEmpty(form.time) || keepIfNonEmpty(seriesData.time) || keepIfNonEmpty(seriesData?.recurrence?.time) || '00:00';
    const duration = Number(form.durationMinutes || seriesData.durationMinutes || 60);
    const {
      start,
      end
    } = toStartEnd(targetOccurrenceId, occurrenceTime, duration);
    const seedPayload = {
      id: occurrenceDocId,
      originalEventId: form.id,
      eventId: occurrenceDocId,
      scope: 'occurrence',
      occurrenceId: targetOccurrenceId,
      name: keepIfNonEmpty(form.name) || keepIfNonEmpty(seriesData.name),
      type: 'oneOff',
      date: targetOccurrenceId,
      time: occurrenceTime,
      durationMinutes: duration,
      tags: Array.isArray(form.tags) ? form.tags : Array.isArray(seriesData.tags) ? seriesData.tags : [],
      eventMode: form.eventMode || seriesData.eventMode || seriesData.mode || 'inPerson',
      onlineUrl: keepIfNonEmpty(form.onlineUrl) || keepIfNonEmpty(seriesData.onlineUrl),
      locationName: keepIfNonEmpty(form.locationName) || keepIfNonEmpty(seriesData.locationName),
      locationStreet: keepIfNonEmpty(form.locationStreet) || keepIfNonEmpty(seriesData.locationStreet),
      locationCity: keepIfNonEmpty(form.locationCity) || keepIfNonEmpty(seriesData.locationCity),
      locationPostcode: keepIfNonEmpty(form.locationPostcode) || keepIfNonEmpty(seriesData.locationPostcode),
      locationCountry: keepIfNonEmpty(form.locationCountry) || keepIfNonEmpty(seriesData.locationCountry),
      attendanceMode: form.attendanceMode || seriesData.attendanceMode || 'none',
      capacity: keepIfNonEmpty(form.capacity) ?? keepIfNonEmpty(seriesData.capacity),
      allowNonMembers: form.allowNonMembers !== undefined ? !!form.allowNonMembers : !!seriesData.allowNonMembers,
      ticketName: keepIfNonEmpty(form.ticketName) || keepIfNonEmpty(seriesData.ticketName),
      ticketCurrency: form.ticketCurrency || seriesData.ticketCurrency || 'GBP',
      ticketPriceMajor: keepIfNonEmpty(form.ticketPriceMajor) ?? keepIfNonEmpty(seriesData.ticketPriceMajor),
      ticketPricePence: form.ticketPricePence !== undefined ? form.ticketPricePence : seriesData.ticketPricePence,
      controlSalesTime: form.controlSalesTime !== undefined ? !!form.controlSalesTime : !!seriesData.controlSalesTime,
      ticketSaleStart: keepIfNonEmpty(form.ticketSaleStart) || keepIfNonEmpty(seriesData.ticketSaleStart),
      ticketSaleEnd: keepIfNonEmpty(form.ticketSaleEnd) || keepIfNonEmpty(seriesData.ticketSaleEnd),
      ticketPlatformFeeHandling: form.ticketPlatformFeeHandling || seriesData.ticketPlatformFeeHandling || 'add',
      stripeProductId: keepIfNonEmpty(form.stripeProductId) || keepIfNonEmpty(seriesData.stripeProductId),
      stripePriceId: keepIfNonEmpty(form.stripePriceId) || keepIfNonEmpty(seriesData.stripePriceId),
      checkoutUrl: keepIfNonEmpty(form.checkoutUrl) || keepIfNonEmpty(seriesData.checkoutUrl),
      roles: normalizeEventRoles(form.roles || seriesData.roles || []),
      ...(start && {
        start
      }),
      ...(end && {
        end
      }),
      recurrence: null,
      updatedAt: serverTimestamp()
    };
    Object.keys(seedPayload).forEach(k => seedPayload[k] === undefined && delete seedPayload[k]);
    await setDoc(occurrenceRef, seedPayload, {
      merge: true
    });
    return occurrenceDocId;
  }, [form.allowNonMembers, form.attendanceMode, form.capacity, form.checkoutUrl, form.controlSalesTime, form.durationMinutes, form.eventMode, form.id, form.locationCity, form.locationCountry, form.locationName, form.locationPostcode, form.locationStreet, form.name, form.onlineUrl, form.roles, form.tags, form.ticketCurrency, form.ticketName, form.ticketPlatformFeeHandling, form.ticketPriceMajor, form.ticketPricePence, form.ticketSaleEnd, form.ticketSaleStart, form.time, form.stripePriceId, form.stripeProductId, keepIfNonEmpty, selectedOccurrenceId, teamId]);
  const updateRoleRequestsForSlot = useCallback(async ({
    slotId,
    approvedRequestId = null,
    eventDocId = roleEventId
  }) => {
    const slotQuery = query(collection(db, 'teams', teamId, 'events', eventDocId, 'roleRequests'), where('slotId', '==', slotId), where('status', '==', 'requested'));
    const snap = await getDocs(slotQuery);
    if (snap.empty) return;
    const batch = writeBatch(db);
    snap.docs.forEach(docSnap => {
      const status = docSnap.id === approvedRequestId ? 'approved' : 'rejected';
      batch.set(docSnap.ref, {
        status,
        reviewedBy: userId || null,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
    });
    await batch.commit();
  }, [roleEventId, teamId, userId]);
  const assignRoleSlot = useCallback(async ({
    roleId,
    slotId,
    assigneeUserId,
    assigneeDisplayName,
    eventDocId = roleEventId
  }) => {
    const eventRef = doc(db, 'teams', teamId, 'events', eventDocId);
    await runTransaction(db, async tx => {
      const snap = await tx.get(eventRef);
      if (!snap.exists()) throw new Error('Event no longer exists.');
      const currentRoles = normalizeEventRoles(snap.data()?.roles || []);
      const nextRoles = applyRoleSlotAssignment(currentRoles, roleId, slotId, {
        assigneeUserId,
        assigneeDisplayName
      });
      tx.set(eventRef, {
        roles: nextRoles,
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
    });
    setForm(prev => ({
      ...prev,
      roles: applyRoleSlotAssignment(normalizeEventRoles(prev.roles), roleId, slotId, {
        assigneeUserId,
        assigneeDisplayName
      })
    }));
  }, [roleEventId, teamId]);
  const handleVolunteer = async ({
    role,
    slot,
    scope = null
  }) => {
    if (!teamId || !form.id || !userId || !slot?.id || !role?.id) return;
    if (!isTeamMember && !isAdmin) {
      alert('You need to be a team member to take this role.');
      return;
    }
    const targetScope = scope === 'occurrence' ? 'occurrence' : scope === 'series' ? 'series' : roleCoverageScope === 'series' ? 'series' : 'event';
    if (targetScope === 'occurrence' && !selectedOccurrenceId) {
      alert('Select an occurrence first.');
      return;
    }
    const allowedTags = Array.isArray(role.allowedTags) ? role.allowedTags : [];
    const eligible = allowedTags.length === 0 || allowedTags.some(tagName => memberTagNames.includes(tagName));
    if (!eligible) {
      alert('You are not eligible for this role.');
      return;
    }
    try {
      setRoleActionBusy(true);
      const actionUrl = targetScope === 'occurrence' && selectedOccurrenceId ? `/teams/${teamId}/calendar?event=${form.id}&occurrence=${selectedOccurrenceId}&panel=roles` : `/teams/${teamId}/calendar?event=${form.id}&panel=roles`;
      if (role.requiresApproval === false) {
        const shouldUseCallable = targetScope === 'occurrence' || !isAdmin;
        const targetEventDocId = targetScope === 'occurrence' ? buildOccurrenceEventDocId(form.id, selectedOccurrenceId) : targetScope === 'series' ? form.id : roleEventId;
        if (shouldUseCallable) {
          const callableEventId = targetScope === 'occurrence' ? form.id : targetEventDocId;
          await takeRoleCallable({
            teamId,
            eventId: callableEventId,
            occurrenceId: targetScope === 'occurrence' ? selectedOccurrenceId : null,
            roleId: role.id,
            slotId: slot.id
          });
        } else {
          const eventRef = doc(db, 'teams', teamId, 'events', targetEventDocId);
          await runTransaction(db, async tx => {
            const eventSnap = await tx.get(eventRef);
            if (!eventSnap.exists()) throw new Error('Event no longer exists.');
            const currentRoles = normalizeEventRoles(eventSnap.data()?.roles || []);
            const targetRole = currentRoles.find(entry => entry.id === role.id);
            if (!targetRole) throw new Error('Role no longer exists.');
            const roleAllowedTags = Array.isArray(targetRole.allowedTags) ? targetRole.allowedTags : [];
            const stillEligible = roleAllowedTags.length === 0 || roleAllowedTags.some(tagName => memberTagNames.includes(tagName));
            if (!stillEligible) throw new Error('You are not eligible for this role.');
            const targetSlot = (targetRole.slots || []).find(entry => entry.id === slot.id);
            if (!targetSlot) throw new Error('Slot no longer exists.');
            if (targetSlot.assigneeUserId) throw new Error('That role slot has already been taken.');
            const nextRoles = applyRoleSlotAssignment(currentRoles, role.id, slot.id, {
              assigneeUserId: userId,
              assigneeDisplayName: currentUserName || 'Member'
            });
            tx.set(eventRef, {
              roles: nextRoles,
              updatedAt: serverTimestamp()
            }, {
              merge: true
            });
          });
        }
        if (targetScope !== 'occurrence' && targetEventDocId === form.id) {
          setForm(prev => ({
            ...prev,
            roles: applyRoleSlotAssignment(normalizeEventRoles(prev.roles), role.id, slot.id, {
              assigneeUserId: userId,
              assigneeDisplayName: currentUserName || 'Member'
            })
          }));
        }
        if (targetEventDocId) {
          await updateRoleRequestsForSlot({
            slotId: slot.id,
            approvedRequestId: null,
            eventDocId: targetEventDocId
          });
        }
        await createNotification({
          userId,
          message: `You are now assigned to ${role.name}.`,
          actionUrl,
          teamId,
          notificationCategory: 'events',
          pushContext: form.name || 'Event',
        });
        return;
      }
      const requestId = `${slot.id}:${userId}`;
      const targetEventDocId = targetScope === 'occurrence' ? isAdmin ? await ensureOccurrenceRoleEventDoc() : buildOccurrenceEventDocId(form.id, selectedOccurrenceId) : targetScope === 'series' ? form.id : roleEventId;
      if (!targetEventDocId) throw new Error('Unable to determine target event.');
      const requestRef = doc(db, 'teams', teamId, 'events', targetEventDocId, 'roleRequests', requestId);
      await setDoc(requestRef, {
        roleId: role.id,
        slotId: slot.id,
        roleName: role.name || '',
        requestedBy: userId,
        requestedByName: currentUserName || 'Member',
        scope: targetScope === 'occurrence' ? 'occurrence' : 'series',
        occurrenceId: targetScope === 'occurrence' ? selectedOccurrenceId : null,
        status: 'requested',
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      }, {
        merge: true
      });
      await notifyTeamAdmins({
        message: targetScope === 'occurrence' ? `Role volunteer request for "${role.name}" on "${form.name}" (this event only)` : `Role volunteer request for "${role.name}" on "${form.name}"`,
        actionUrl
      });
      await createNotification({
        userId,
        message: `Request submitted for ${role.name}.`,
        actionUrl,
        teamId,
        notificationCategory: 'events',
        pushContext: form.name || 'Event',
      });
    } catch (err) {
      alert(err?.message || 'Unable to submit role request right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  const handleWithdrawRequest = async request => {
    const targetRequestEventDocId = request?.eventDocId || roleEventId;
    if (!request?.id || !teamId || !targetRequestEventDocId) return;
    try {
      setRoleActionBusy(true);
      await setDoc(doc(db, 'teams', teamId, 'events', targetRequestEventDocId, 'roleRequests', request.id), {
        status: 'withdrawn',
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
    } catch (err) {
      alert('Unable to withdraw request right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  const handleApproveRequest = async request => {
    if (!isAdmin || !request?.slotId || !request?.roleId || !request?.requestedBy) return;
    const isOccurrenceRequest = request?.scope === 'occurrence' || (selectedOccurrenceRoleEventId && request?.eventDocId === selectedOccurrenceRoleEventId);
    const targetRequestEventDocId = request?.eventDocId || (isOccurrenceRequest ? selectedOccurrenceRoleEventId : roleEventId);
    if (!targetRequestEventDocId) return;
    if (!isOccurrenceRequest && !confirmSeriesRoleAction('Approving this volunteer')) return;
    try {
      setRoleActionBusy(true);
      let targetAssignEventDocId = targetRequestEventDocId;
      const requestOccurrenceId = (() => {
        if (typeof request?.occurrenceId === 'string' && request.occurrenceId.trim() !== '') {
          return request.occurrenceId.trim();
        }
        const docId = String(targetRequestEventDocId || '');
        const match = docId.match(/__(\d{4}-\d{2}-\d{2})$/);
        return match ? match[1] : null;
      })();
      if (isOccurrenceRequest && requestOccurrenceId) {
        targetAssignEventDocId = await ensureOccurrenceRoleEventDoc(requestOccurrenceId);
      }
      const memberName = teamMembers.find(member => member.id === request.requestedBy)?.name || request.requestedByName || 'Member';
      await assignRoleSlot({
        roleId: request.roleId,
        slotId: request.slotId,
        assigneeUserId: request.requestedBy,
        assigneeDisplayName: memberName,
        eventDocId: targetAssignEventDocId
      });
      await updateRoleRequestsForSlot({
        slotId: request.slotId,
        approvedRequestId: request.id,
        eventDocId: targetAssignEventDocId
      });
      const notifyOccurrenceId = isOccurrenceRequest && (requestOccurrenceId || selectedOccurrenceId) ? requestOccurrenceId || selectedOccurrenceId : null;
      const actionUrl = notifyOccurrenceId ? `/teams/${teamId}/calendar?event=${form.id}&occurrence=${notifyOccurrenceId}` : `/teams/${teamId}/calendar?event=${form.id}`;
      await createNotification({
        userId: request.requestedBy,
        message: `Your role request for ${request.roleName || 'event role'} was approved.`,
        actionUrl,
        teamId,
        notificationCategory: 'events',
        pushContext: form.name || 'Event',
      });
    } catch (err) {
      alert('Unable to approve this request right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  const handleRejectRequest = async request => {
    if (!isAdmin || !request?.id) return;
    const isOccurrenceRequest = request?.scope === 'occurrence' || (selectedOccurrenceRoleEventId && request?.eventDocId === selectedOccurrenceRoleEventId);
    const targetRequestEventDocId = request?.eventDocId || (isOccurrenceRequest ? selectedOccurrenceRoleEventId : roleEventId);
    if (!targetRequestEventDocId) return;
    if (!isOccurrenceRequest && !confirmSeriesRoleAction('Rejecting this volunteer')) return;
    try {
      setRoleActionBusy(true);
      await setDoc(doc(db, 'teams', teamId, 'events', targetRequestEventDocId, 'roleRequests', request.id), {
        status: 'rejected',
        reviewedBy: userId || null,
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, {
        merge: true
      });
      if (request.requestedBy) {
        const notifyOccurrenceId = isOccurrenceRequest && (request?.occurrenceId || selectedOccurrenceId) ? request?.occurrenceId || selectedOccurrenceId : null;
        const actionUrl = notifyOccurrenceId ? `/teams/${teamId}/calendar?event=${form.id}&occurrence=${notifyOccurrenceId}` : `/teams/${teamId}/calendar?event=${form.id}`;
        await createNotification({
          userId: request.requestedBy,
          message: `Your role request for ${request.roleName || 'event role'} was rejected.`,
          actionUrl,
          teamId,
          notificationCategory: 'events',
          pushContext: form.name || 'Event',
        });
      }
    } catch (err) {
      alert('Unable to reject this request right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  const handleAssignMember = async ({
    role,
    slot,
    memberId,
    scope = null
  }) => {
    if (!isAdmin || !role?.id || !slot?.id || !memberId) return;
    const targetScope = scope === 'occurrence' ? 'occurrence' : scope === 'series' ? 'series' : roleCoverageScope === 'series' ? 'series' : 'event';
    if (targetScope === 'series' && !confirmSeriesRoleAction('Assigning this role')) return;
    if (targetScope === 'occurrence' && !selectedOccurrenceId) {
      alert('Select an occurrence first.');
      return;
    }
    try {
      setRoleActionBusy(true);
      const targetEventDocId = targetScope === 'occurrence' ? await ensureOccurrenceRoleEventDoc() : targetScope === 'series' ? form.id : roleEventId;
      const memberName = teamMembers.find(member => member.id === memberId)?.name || 'Member';
      const matchingRequest = targetScope === 'series' ? pendingRoleRequests.find(request => request.eventDocId === targetEventDocId && request.roleId === role.id && request.slotId === slot.id && request.requestedBy === memberId && (request.status || 'requested') === 'requested') : null;
      await assignRoleSlot({
        roleId: role.id,
        slotId: slot.id,
        assigneeUserId: memberId,
        assigneeDisplayName: memberName,
        eventDocId: targetEventDocId
      });
      if (targetScope === 'series') {
        await updateRoleRequestsForSlot({
          slotId: slot.id,
          approvedRequestId: role.requiresApproval === false ? null : matchingRequest?.id || null,
          eventDocId: targetEventDocId
        });
      }
      const actionUrl = targetScope === 'occurrence' && selectedOccurrenceId ? `/teams/${teamId}/calendar?event=${form.id}&occurrence=${selectedOccurrenceId}` : `/teams/${teamId}/calendar?event=${form.id}`;
      await createNotification({
        userId: memberId,
        message: `You were assigned ${role.name} for ${form.name}.`,
        actionUrl,
        teamId,
        notificationCategory: 'events',
        pushContext: form.name || 'Event',
      });
    } catch (err) {
      alert('Unable to assign this slot right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  const handleClearAssignment = async ({
    role,
    slot,
    scope = null
  }) => {
    if (!isAdmin || !role?.id || !slot?.id) return;
    const targetScope = scope === 'occurrence' ? 'occurrence' : scope === 'series' ? 'series' : roleCoverageScope === 'series' ? 'series' : 'event';
    if (targetScope === 'series' && !confirmSeriesRoleAction('Clearing this assignment')) return;
    if (targetScope === 'occurrence' && !selectedOccurrenceId) {
      alert('Select an occurrence first.');
      return;
    }
    try {
      setRoleActionBusy(true);
      const targetEventDocId = targetScope === 'occurrence' ? await ensureOccurrenceRoleEventDoc() : targetScope === 'series' ? form.id : roleEventId;
      await assignRoleSlot({
        roleId: role.id,
        slotId: slot.id,
        assigneeUserId: null,
        assigneeDisplayName: '',
        eventDocId: targetEventDocId
      });
    } catch (err) {
      alert('Unable to clear assignment right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  const handleDutyResponse = async ({
    role,
    slot,
    status,
    scope = null
  }) => {
    if (!teamId || !role?.id || !slot?.id || !status) return;
    const targetScope = scope === 'occurrence' ? 'occurrence' : scope === 'series' ? 'series' : roleCoverageScope === 'series' ? 'series' : 'event';
    if (targetScope === 'occurrence' && !selectedOccurrenceId) {
      alert('Select an occurrence first.');
      return;
    }
    const targetEventDocId = targetScope === 'occurrence' ? buildOccurrenceEventDocId(form.id, selectedOccurrenceId) : targetScope === 'series' ? form.id : roleEventId;
    try {
      setRoleActionBusy(true);
      await respondDutyCallable({
        teamId,
        eventId: targetEventDocId,
        roleId: role.id,
        slotId: slot.id,
        status
      });
    } catch (err) {
      alert(err?.message || 'Unable to update this duty response right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  const handleDutySwapRequest = async ({
    role,
    slot,
    scope = null
  }) => {
    if (!teamId || !role?.id || !slot?.id) return;
    const targetScope = scope === 'occurrence' ? 'occurrence' : scope === 'series' ? 'series' : roleCoverageScope === 'series' ? 'series' : 'event';
    if (targetScope === 'occurrence' && !selectedOccurrenceId) {
      alert('Select an occurrence first.');
      return;
    }
    const targetEventDocId = targetScope === 'occurrence' ? buildOccurrenceEventDocId(form.id, selectedOccurrenceId) : targetScope === 'series' ? form.id : roleEventId;
    try {
      setRoleActionBusy(true);
      await createDutySwapCallable({
        teamId,
        eventId: targetEventDocId,
        roleId: role.id,
        slotId: slot.id
      });
      alert('Swap request created.');
    } catch (err) {
      alert(err?.message || 'Unable to create a swap request right now.');
    } finally {
      setRoleActionBusy(false);
    }
  };
  if (!isOpen) return null;
  const modalThemeVars = {
    ...(teamBrand?.primaryColor && {
      '--team-primary': teamBrand.primaryColor
    }),
    ...(teamBrand?.secondaryColor && {
      '--team-secondary': teamBrand.secondaryColor
    }),
    ...(teamBrand?.headingTextColor && {
      '--team-heading-text': teamBrand.headingTextColor
    }),
    ...(teamBrand?.bodyTextColor && {
      '--team-body-text': teamBrand.bodyTextColor
    })
  };
  if (readOnlyComputed) {
    const rsvpEnabled = form.attendanceMode === 'rsvp' && !!userId;
    const showTicketBlock = isTicketed && !!userId;
    const handleUserRefundRequest = async () => {
      try {
        const paidTicket = (ticketing?.tickets || []).find(t => t.status === 'paid' || t.status === 'partially_refunded');
        if (!paidTicket) {
          alert('No paid tickets found for this event.');
          return;
        }
        setRefundRequested(true);
        const reqId = `${paidTicket.id}:${userId}`;
        await setDoc(doc(db, 'teams', teamId, 'events', form.id, 'refundRequests', reqId), {
          ticketId: paidTicket.id,
          userId,
          status: 'requested',
          createdAt: serverTimestamp()
        }, {
          merge: true
        });
        await setDoc(doc(db, 'teams', teamId, 'events', form.id, 'tickets', paidTicket.id), {
          refundRequestedAt: serverTimestamp(),
          refundRequestedBy: userId,
          refundStatus: 'requested'
        }, {
          merge: true
        });
        await notifyTeamAdmins({
          message: `Refund requested for "${form.name}"`,
          actionUrl: `/teams/${teamId}/calendar?event=${form.id}`
        });
        alert('Refund request sent to the organiser.');
      } catch (err) {
        alert('Sorry, failed to request a refund.');
      }
    };
    const roles = normalizeEventRoles(form.roles);
    const outingSummary = linkedOutingCrews.reduce((acc, crew) => {
      const status = String(crew?.status || 'draft').toLowerCase();
      if (status === 'allocated') acc.allocated += 1;
      else if (status === 'unfilled') acc.unfilled += 1;
      else if (status === 'pending') acc.pending += 1;
      else acc.draft += 1;
      return acc;
    }, {
      draft: 0,
      pending: 0,
      allocated: 0,
      unfilled: 0
    });
    const myOutingCrews = linkedOutingCrews.filter(crew => (Array.isArray(crew?.memberIds) ? crew.memberIds : []).includes(userId));
    const shouldAutoOpenOuting = isAdmin ? linkedOutingCrews.length > 0 : myOutingCrews.length > 0;
    const unfulfilledRoleCount = roles.reduce((count, role) => {
      const allowedTags = Array.isArray(role?.allowedTags) ? role.allowedTags : [];
      const memberCanCoverRole =
        isAdmin ||
        (isTeamMember && (
          allowedTags.length === 0 ||
          allowedTags.some(tagName => memberTagNames.includes(tagName))
        ));
      if (!memberCanCoverRole) return count;
      const slots = Array.isArray(role?.slots) ? role.slots : [];
      return count + slots.filter(slot => !slot?.assigneeUserId).length;
    }, 0);
    const assignedRoleCount = roles.reduce((count, role) => {
      const slots = Array.isArray(role?.slots) ? role.slots : [];
      return count + slots.filter(slot => slot?.assigneeUserId === userId).length;
    }, 0);
    const shouldAutoOpenRoles = unfulfilledRoleCount > 0 || assignedRoleCount > 0;
    const outingBlock = linkedOutingLoading ? <div className="card">
          <div className="card-body">Loading outing status…</div>
        </div> : linkedOuting ? <div className="card">
          <div className="card-header">
            <h3 style={{
          margin: 0
        }}>Outing</h3>
            <div className="card-subtitle">
              {String(linkedOuting?.status || '').toLowerCase() === 'submitted' ? 'Shared allocation status for this outing.' : 'Admin draft linked to this event.'}
            </div>
          </div>
          <div className="card-body" style={{
        display: 'grid',
        gap: 10
      }}>
            <div className="text-muted" style={{
          fontSize: '0.95rem'
        }}>
              {linkedOuting.name || linkedOuting.linkedEventName || 'Outing'} • {outingSummary.pending} pending • {outingSummary.allocated} allocated • {outingSummary.unfilled} no boat
            </div>
            {(isAdmin ? linkedOutingCrews : myOutingCrews).length === 0 ? <p className="text-muted" style={{
          margin: 0
        }}>
                {isAdmin ? 'No crews saved for this outing yet.' : 'You are not currently assigned to an outing crew for this event.'}
              </p> : <div style={{
          display: 'grid',
          gap: 8
        }}>
                {(isAdmin ? linkedOutingCrews : myOutingCrews).map(crew => {
              const members = Array.isArray(crew?.members) ? crew.members : [];
              const memberSummary = members.map(member => {
                const seatLabel = typeof member?.seatLabel === 'string' && member.seatLabel.trim() !== '' ? ` (${member.seatLabel.trim()})` : '';
                return `${member?.displayName || 'Member'}${seatLabel}`;
              }).join(', ');
              const status = String(crew?.status || 'draft').toLowerCase();
              const boatLabel = (crew?.selectedBoatName || crew?.selectedBoatId) ? `Allocated to ${crew?.selectedBoatName || crew?.selectedBoatId}` : status === 'pending' ? 'Pending allocation' : status === 'unfilled' ? 'No boat allocated' : 'Draft';
              return <div key={crew.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                padding: '0.7rem 0.8rem'
              }}>
                      <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'start'
                }}>
                        <div>
                          <strong>{crew?.label || 'Crew'}</strong>
                          <div className="text-muted" style={{
                      marginTop: 4,
                      fontSize: '0.9rem'
                    }}>
                            {boatLabel}
                          </div>
                        </div>
                        <span className={`status-pill ${status === 'allocated' ? 'success' : status === 'unfilled' ? 'danger' : 'info'}`}>
                          {status === 'allocated' ? 'Allocated' : status === 'unfilled' ? 'No boat' : status === 'pending' ? 'Pending' : 'Draft'}
                        </span>
                      </div>
                      {memberSummary ? <div style={{
                  marginTop: 8,
                  fontSize: '0.95rem'
                }}>
                          {memberSummary}
                        </div> : null}
                    </div>;
            })}
              </div>}
          </div>
        </div> : null;
    const commentsEventId = form.originalEventId || form.originalId || form.id;
    const commentsOccurrenceId = eventCommentOccurrenceId;
    const commentsBlock = isExisting && commentsEventId && !eventCommentIdentityLoading && (teamEventCommentsEnabled || isAdmin) ? <EventCommentsSection teamId={teamId} eventId={commentsEventId} occurrenceId={commentsOccurrenceId} commentsEnabled={teamEventCommentsEnabled} isAdmin={isAdmin} canModerateComments={canModerateEventComments} onCommentsEnabledChange={setTeamEventCommentsEnabled} /> : null;
    const activeRsvpOccurrenceId = selectedOccurrenceId || form.occurrenceId || occurrenceId || null;
    const currentMemberName = teamMembers.find(member => member.id === userId)?.name || '';
    const extraRsvpRows = rsvpEnabled && userId ? [
      mySeriesRsvpStatus ? {
        id: userId,
        memberId: userId,
        displayName: currentMemberName,
        status: mySeriesRsvpStatus,
        scope: 'series',
        updatedAt: new Date().toISOString()
      } : null,
      activeRsvpOccurrenceId && myOccurrenceRsvpStatus ? {
        id: `${userId}__${activeRsvpOccurrenceId}`,
        memberId: userId,
        displayName: currentMemberName,
        status: myOccurrenceRsvpStatus,
        scope: 'occurrence',
        occurrenceId: activeRsvpOccurrenceId,
        updatedAt: new Date().toISOString()
      } : null
    ].filter(Boolean) : [];
    const rsvpReadOnly = !isAdmin && rsvpDeadlineClosed;
    const rsvpReadOnlyMessage = rsvpDeadlineText ? `The RSVP deadline was ${rsvpDeadlineText}.` : 'The RSVP deadline has passed.';
    return <ReadOnlyEventView form={form} isExisting={isExisting} isAdmin={isAdmin} canViewAttendees={canViewAttendees} attendeesReadOnly={!isAdmin && !canManageEventRefunds} canManageEventRefunds={canManageEventRefunds} onCopy={onCopy} onEdit={() => {
      setEditScopeIntent('event');
      setEditMode(true);
    }} onEditOccurrence={isRecurringEvent ? () => {
      setEditScopeIntent('occurrence');
      setEditMode(true);
    } : undefined} onEditSeries={isRecurringEvent ? () => {
      setEditScopeIntent('series');
      setEditMode(true);
    } : undefined} onClose={onClose} modalStyle={modalThemeVars} myRsvpStatus={myRsvpStatus} myRsvpOccurrenceStatus={myOccurrenceRsvpStatus} myRsvpSeriesStatus={mySeriesRsvpStatus} rsvpSeriesResponsesEnabled={teamRsvpSeriesResponsesEnabled} calendarSurface={calendarSurface} rsvpYesCount={currentYesCount} rsvpCapacityNum={capacityNum} rsvpIsFull={isFull} rolesBlock={roles.length > 0 ? <RolesReadOnly roles={roles} isAdmin={isAdmin} isTeamMember={isTeamMember || isAdmin} currentUserId={userId} memberTagNames={memberTagNames} myRequestsBySlot={myRequestsBySlot} myRequestsByScopeSlot={myRequestsByScopeSlot} pendingRequests={pendingRoleRequests} memberOptions={teamMembers} onVolunteer={roleActionBusy ? undefined : handleVolunteer} onWithdraw={roleActionBusy ? undefined : handleWithdrawRequest} onApprove={roleActionBusy ? undefined : handleApproveRequest} onReject={roleActionBusy ? undefined : handleRejectRequest} onDutyResponse={roleActionBusy ? undefined : handleDutyResponse} onSwapRequest={roleActionBusy ? undefined : handleDutySwapRequest} onAssign={roleActionBusy ? undefined : ({
      role,
      slot,
      memberId,
      scope
    }) => handleAssignMember({
      role,
      slot,
      memberId,
      scope
    })} onClear={roleActionBusy ? undefined : handleClearAssignment} busy={roleActionBusy} coverageScope={roleCoverageScope} coverageHint={roleCoverageHint} allowScopeChoice={allowRoleScopeChoice} /> : null} rsvpBlock={!showTicketBlock && rsvpEnabled ? <>
              <RsvpControls enabled title="Your RSVP" isFull={isFull} myRsvpStatus={myRsvpStatus} myOccurrenceRsvpStatus={myOccurrenceRsvpStatus} mySeriesRsvpStatus={mySeriesRsvpStatus} isRecurring={isRecurring} occurrenceId={occurrenceId} setRsvpStatus={setRsvpStatus} resetRsvpStatus={resetRsvpStatus} rsvpBusy={rsvpBusy} capacityNum={capacityNum} currentYesCount={currentYesCount} capacityRemaining={capacityRemaining} rsvpSeriesResponsesEnabled={teamRsvpSeriesResponsesEnabled} readOnly={rsvpReadOnly} readOnlyMessage={rsvpReadOnlyMessage} />
              <FamilyMemberRsvpPanels form={attendanceForm} teamId={teamId} actorUserId={userId} occurrenceIdOverride={selectedOccurrenceId} rsvpSeriesResponsesEnabled={teamRsvpSeriesResponsesEnabled} members={familyRsvpMembers} readOnly={rsvpReadOnly} readOnlyMessage={rsvpReadOnlyMessage} />
              <EventWaitlistControls form={attendanceForm} teamId={teamId} userId={userId} isAdmin={isAdmin} occurrenceId={selectedOccurrenceId || occurrenceId || ''} isFull={isFull} />
            </> : null} ticketBlock={showTicketBlock ? <div><TicketControls status={ticketing.status} busy={ticketing.busy} paidCount={ticketing.paidCount} pendingCount={ticketing.pendingCount} startCheckout={ticketing.startCheckout} loadError={ticketing.loadError} admissionTicketsEnabled={form.admissionTicketsEnabled === true} bookingConfig={{ ...form, teamId, eventId: form.id }} ticketMeta={{
      name: ticketing.ticketMeta?.name || fallbackTicketMeta.name,
      currency: ticketing.ticketMeta?.currency || fallbackTicketMeta.currency,
      price: ticketPricing?.buyerPays ?? ticketing.ticketMeta?.price ?? fallbackTicketMeta.price
    }} pricing={ticketPricing} eventStartTime={eventStartTime} coverageLabel={isRecurringEvent ? (form.ticketPaymentBasis === 'whole_series' ? 'for the remaining series' : 'per occurrence') : ''} checkoutUnavailableMessage={checkoutUnavailableMessage} payoutTimingText={adminPayoutTimingText} onRequestRefund={handleUserRefundRequest} refundRequested={refundRequested} /><EventWaitlistControls form={form} teamId={teamId} userId={userId} isAdmin={isAdmin} occurrenceId={selectedOccurrenceId || occurrenceId || ''} isFull={isFull} startCheckout={ticketing.startCheckout} /></div> : null} outingBlock={outingBlock} outingAutoOpen={shouldAutoOpenOuting} commentsBlock={commentsBlock} extraRsvpRows={extraRsvpRows} commentsAutoOpen={teamEventCommentsEnabled && eventCommentsHaveActivity} rolesAutoOpen={shouldAutoOpenRoles} rolesUnfilledCount={unfulfilledRoleCount} resolvingPriority={(rsvpEnabled && rsvpLoading && !rsvpBusy) || linkedOutingLoading || teamEventCommentsLoading || eventCommentIdentityLoading || eventCommentsActivityLoading} focusComments={focusComments} db={db} teamId={teamId} />;
  }
  return <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-content--xl event-modal" style={modalThemeVars} onClick={e => e.stopPropagation()}>
        <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'start',
        gap: 12
      }}>
          <h2 className="modal-title">{isExisting ? 'Edit Event' : isCopy ? 'Copy Event' : 'Create New Event'}</h2>
          <div style={{
          display: 'flex',
          gap: 8
        }}>
            <SubmitButton type="button" label="Cancel" onClick={onClose} variant="ghost" size="sm" />
            {isExisting && (!hasPaidTickets || isOccurrenceDeleteIntent) && <SubmitButton type="button" label={isOccurrenceDeleteIntent ? 'Delete occurrence' : isRecurringEvent ? 'Delete series' : 'Delete'} onClick={handleDelete} variant="danger" size="sm" />}
          </div>
        </div>

        <form className="modal-form form-sections" onSubmit={handleSave}>
          {isCopy && <div className="card" style={{
          border: '1px solid #bfdbfe',
          background: '#eff6ff',
          marginBottom: 12
        }}>
              <div className="card-body" style={{ color: '#1e3a8a' }}>
                Details were copied from “{initialData?._copySourceName || 'Event'}”. Choose the new date and review the settings before creating it. Attendees, RSVPs, ticket purchases, and role assignments are not copied.
              </div>
            </div>}
          {isOccurrenceScopeEditing && <div className="card" style={{
          border: '1px solid #93c5fd',
          background: '#eff6ff',
          marginBottom: 12
        }}>
              <div className="card-body" style={{
            color: '#1e3a8a'
          }}>
                Editing a single occurrence override. Saving updates only this occurrence.
              </div>
            </div>}
          <SectionCard title="Basics">
            <BasicFields form={form} onChange={handleChange} lockType={isOccurrenceScopeEditing || (hasPaidTickets && isRecurringEvent)} lockTypeLabel={isOccurrenceScopeEditing ? 'Single occurrence override' : form.type === 'recurring' ? 'Recurring' : 'One-Off'} />
          </SectionCard>

          <SectionCard title="Recurrence">
            <RecurrenceFields form={form} onChange={handleChange} forceOccurrence={isOccurrenceScopeEditing} lockedOccurrenceDate={selectedOccurrenceId} locked={hasPaidTickets && isRecurringEvent && !isOccurrenceScopeEditing} />
          </SectionCard>

          <SectionCard title="Tags">
            <TagsSelector tags={tags} selected={form.tags} onToggle={handleTagToggle} onClear={() => setForm(prev => ({
            ...prev,
            tags: []
          }))} />
          </SectionCard>

          <SectionCard title="Where & How">
            <EventModeFields form={form} onChange={handleChange} locations={locations} />
          </SectionCard>

          <SectionCard title="Event Roles" subtitle="Add role slots and optionally publish new presets.">
            <button type="button" onClick={() => setRolesEditorExpanded(open => !open)} aria-expanded={rolesEditorExpanded} style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            padding: '0.55rem 0.7rem',
            cursor: 'pointer'
          }}>
              <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8
            }}>
                <span aria-hidden="true">{rolesEditorExpanded ? '▾' : '▸'}</span>
                <span style={{
                fontWeight: 600
              }}>
                  {rolesEditorExpanded ? 'Hide roles editor' : 'Show roles editor'}
                </span>
              </span>
              <span className="text-muted" style={{
              fontSize: 12
            }}>
                {normalizeEventRoles(form.roles).length} role{normalizeEventRoles(form.roles).length === 1 ? '' : 's'}
              </span>
            </button>

            {rolesEditorExpanded && <div style={{
            marginTop: 10
          }}>
                <RolesEditor roles={normalizeEventRoles(form.roles)} presetRoles={eventRoles} teamTags={tags} memberOptions={teamMembers} onAddPreset={addRoleFromPreset} onQuickAdd={quickAddRole} onUpdateRole={updateRole} onResizeSlots={resizeRole} onToggleAllowedTag={toggleRoleAllowedTag} onAssignSlot={assignRoleInForm} onClearSlot={clearRoleInForm} onRemoveRole={removeRole} coverageScope={roleCoverageScope} coverageHint={roleCoverageHint} />
              </div>}
          </SectionCard>

          {isTicketed ? <SectionCard title="Ticketing">
              <TicketingSection teamId={teamId} form={form} onChange={handleChange} commercialLocked={isOccurrenceScopeEditing} paymentBasisLocked={hasPaidTickets} />
            </SectionCard> : <>
              <SectionCard title="Attendance">
                <AttendanceSection form={form} onChange={handleChange} />
              </SectionCard>
              {form.attendanceMode === 'rsvp' ? <SectionCard title="RSVP deadline" subtitle="Close responses and remind members who have not answered.">
                  <RsvpDeadlineSection form={form} onChange={handleChange} isRecurring={isRecurringEvent} selectedOccurrenceId={selectedOccurrenceId} />
                </SectionCard> : null}
            </>}

          {isAdmin && isExisting && <>
              <EventRegister key={`${attendanceEventId}:${selectedOccurrenceId || ''}`} teamId={teamId} form={{ ...form, id: attendanceEventId, occurrenceId: selectedOccurrenceId || form.occurrenceId || '' }} />
              <AttendeesSection db={db} teamId={teamId} eventId={attendanceEventId} attendanceMode={form.attendanceMode} occurrenceId={selectedOccurrenceId} isRecurring={isRecurringEvent} ticketPaymentBasis={form.ticketPaymentBasis || ''} canManageRefunds={canManageEventRefunds} rsvpSeriesResponsesEnabled={teamRsvpSeriesResponsesEnabled} calendarSurface={calendarSurface} />
              <RefundsSection db={db} teamId={teamId} eventId={attendanceEventId} />
              <CancelEventSection
                teamId={teamId}
                eventId={form.originalEventId || form.id}
                occurrenceId={selectedOccurrenceId || form.occurrenceId || ''}
                isRecurring={isRecurringEvent}
                occurrenceScope={isOccurrenceScopeEditing || isOccurrenceOverrideDoc}
                onCancelled={onClose}
              />
            </>}

          <ErrorMessage message={saveError} />
          <div style={{
          marginTop: 16,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
            <SubmitButton type="submit" label={isSaving ? 'Saving...' : isExisting ? 'Update' : isCopy ? 'Create copy' : 'Create'} variant="primary" disabled={isSaving} />
          </div>
        </form>
      </div>
      {stripeGuardDialog}
    </div>;
}
